import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Braces,
  Building2,
  CheckCircle2,
  ClipboardList,
  Code2,
  FileCheck2,
  FileText,
  Fingerprint,
  KeyRound,
  Landmark,
  Layers3,
  LifeBuoy,
  LockKeyhole,
  MapPin,
  Megaphone,
  Network,
  Search,
  ShieldCheck,
  Sparkles,
  UserRoundPlus,
  UsersRound,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import type { LucideIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type DocRole =
  | 'Tenant admin'
  | 'HR'
  | 'Manager'
  | 'Employee'
  | 'CRM operator'
  | 'CRM Administrator'
  | 'Developer'
  | 'Superadmin';
type RoleFilter = 'All roles' | DocRole;

type DocTopic = {
  id: string;
  title: string;
  description: string;
  category: string;
  role: DocRole;
  path: string;
  updated: string;
  icon: LucideIcon;
  keywords: readonly string[];
  steps: readonly string[];
  checks: readonly string[];
  references: readonly string[];
};

type ApiReference = {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'Internal';
  path: string;
  owner: string;
  purpose: string;
};

type TroubleGuide = {
  title: string;
  symptom: string;
  resolution: string;
};

const roleFilters = [
  'All roles',
  'Tenant admin',
  'HR',
  'Manager',
  'Employee',
  'CRM operator',
  'CRM Administrator',
  'Developer',
  'Superadmin',
] as const satisfies readonly RoleFilter[];

const navigationSections = [
  { id: 'quickstart', label: 'Quick start' },
  { id: 'guides', label: 'Guides' },
  { id: 'api', label: 'API reference' },
  { id: 'security', label: 'Security' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
] as const;

const quickStartSteps = [
  {
    title: 'Create the tenant shell',
    owner: 'Tenant admin',
    body: 'Public signup and platform provisioning create the workspace and first admin identity only. They do not create an employee row, legal entity, location, or employee-code sequence.',
    icon: Building2,
  },
  {
    title: 'Model the company foundation',
    owner: 'HR',
    body: 'Choose single company or group company, then create legal entities, locations, departments, designations, positions, and employee-code rules in that order.',
    icon: Layers3,
  },
  {
    title: 'Hire employees after setup',
    owner: 'HR',
    body: 'Create employee master records only after the foundation is active. The first active employee completes the dashboard employee step; an admin account becomes headcount only when HR creates a linked employee profile for that account.',
    icon: UserRoundPlus,
  },
  {
    title: 'Secure and operate',
    owner: 'Tenant admin',
    body: 'Configure MFA, passkeys, role approvals, email verification, document templates, joining kits, events, and audit evidence before production rollout.',
    icon: ShieldCheck,
  },
] as const;

const docTopics = [
  {
    id: 'tenant-signup',
    title: 'Tenant signup and first admin',
    description:
      'Register a workspace without accidentally turning the first admin into an employee or consuming an employee code.',
    category: 'Onboarding',
    role: 'Tenant admin',
    path: '/signup',
    updated: '2026-06-15',
    icon: Building2,
    keywords: [
      'signup',
      'tenant',
      'first admin',
      'admin identity',
      'employee code',
      'company setup',
      'linked employee profile',
    ],
    steps: [
      'Open `/signup` and submit company name, tenant slug, company email, admin name, admin email, locale, timezone, and currency.',
      'Verify company and admin email from the configured email provider. In local development, console email output can be used when provider credentials are not approved.',
      'Sign in as the first admin only after verification completes. Phone verification may remain disabled with `REQUIRE_PHONE_VERIFICATION=false` until SMS DLT templates are approved.',
      'Open tenant setup before creating employees. The admin identity remains an account and can later be linked to a new employee record from `Settings > Users & Security > Users` or the admin profile.',
    ],
    checks: [
      'Tenant status is active after verification.',
      'No legal entity, employee row, or employee-code sequence is created by signup or platform provisioning.',
      'Tenant setup shows HRMS foundation pending and baseline MFA/passkey auth configuration exists before employee creation.',
      'First admin can access setup surfaces but is not counted as employee headcount.',
      '`Create linked employee profile` creates one employee row for an active unlinked user account and does not provision a second auth identity.',
      'The dashboard employee checklist step completes only after the first active employee record exists.',
    ],
    references: [
      'README.md#current-tenant-signup-behavior',
      'docs/plans/2026-06-15-linked-admin-employee-profile-closeout.md',
      'docs/plans/2026-06-08-tenant-signup-admin-identity-public-docs-closeout.md',
    ],
  },
  {
    id: 'linked-admin-employee-profile',
    title: 'Create a linked employee profile for an admin',
    description:
      'Turn an existing tenant user account into an employee profile only when that person should count as workforce headcount.',
    category: 'Employee setup',
    role: 'Tenant admin',
    path: '/settings/users',
    updated: '2026-06-15',
    icon: UserRoundPlus,
    keywords: [
      'linked employee profile',
      'admin employee',
      'first admin',
      'tenant user',
      'auth user',
      'employee headcount',
      'settings users',
      'profile',
    ],
    steps: [
      'Complete company foundation, employee-code prefix, location, department, designation, and position setup before creating the employee profile.',
      'Open `Settings > Users & Security > Users` and search by name, email, phone, role, status, or employee-profile state instead of pasting a UUID.',
      'Open the active unlinked user account action menu and choose `Create linked employee profile`.',
      'Complete the normal employee form. Name, email, and phone are prefilled from the selected user account, but HR must still choose the tenant-owned employment foundation records.',
      'The current admin can also open `Settings > Profile` and use `Create linked employee profile` when their own account has no employee profile.',
    ],
    checks: [
      'The action appears only for active users without an employee profile and only to users with employee-create permission.',
      'The backend validates tenant ownership, inactive users, and duplicate links before writing `Employee.authUserId`.',
      'The employee-created event carries the existing auth user ID, so auth role sync assigns employee access without creating another sign-in identity.',
      'If an existing employee already has a different auth identity, use a separate audited identity-handover workflow instead of this create action.',
    ],
    references: [
      'README.md#current-tenant-signup-behavior',
      'docs/docs/wiki/Operations.md#linked-employee-profile-operations',
      'docs/plans/2026-06-15-linked-admin-employee-profile-closeout.md',
    ],
  },
  {
    id: 'company-foundation',
    title: 'Company foundation for single or group tenants',
    description:
      'Build the legal structure before HR master data so location, department, designation, and position validation has a tenant-owned parent.',
    category: 'Company setup',
    role: 'Tenant admin',
    path: '/companies',
    updated: '2026-06-15',
    icon: Landmark,
    keywords: [
      'legal entity',
      'group company',
      'single company',
      'company code',
      'foundation objects',
      'associations',
    ],
    steps: [
      'Choose whether the tenant operates as one company or a group of legal entities.',
      'Open `/companies`; for a single-company tenant use `Use Tenant Profile`, or use `Add Company` for a parent company or subsidiary.',
      'Create the primary legal entity with registration, country, currency, timezone, and payroll/statutory defaults.',
      'For group tenants, add subsidiary legal entities before creating locations or positions.',
      'Keep inactive or future legal entities out of employee creation until their effective dates and employee-code rules are ready.',
    ],
    checks: [
      'Each legal entity is tenant-owned and has a unique company code.',
      'Location choices include shared tenant-wide offices plus locations attached to the selected legal entity.',
      'Employee creation rejects cross-tenant or inactive foundation records server-side.',
    ],
    references: [
      'docs/docs/wiki/Operations.md',
      'docs/plans/2026-06-08-hrms-basic-readiness-status.md',
    ],
  },
  {
    id: 'locations',
    title: 'Locations, timezone, and statutory context',
    description:
      'Create shared tenant-wide or company-specific work locations with structured address, postal code, timezone, attendance, leave, and payroll defaults.',
    category: 'Company setup',
    role: 'HR',
    path: '/locations',
    updated: '2026-07-13',
    icon: MapPin,
    keywords: [
      'location',
      'timezone',
      'work location',
      'postal code',
      'zip code',
      'pin code',
      'statutory',
      'attendance',
      'leave',
      'payroll',
    ],
    steps: [
      'Add a shared office without a legal entity when multiple companies use the same site, or select a legal entity for a company-exclusive site.',
      'Use the location master counters, scope filters, and search by name, code, address, city, postal code, company, timezone, policy, shift, type, or status.',
      'Record the street, city, state, country, and Postal / ZIP Code before selecting the IANA timezone and default attendance policy.',
      'Use the location when creating positions and employee job information.',
      'Review mobile layouts for location forms because address fields and filters are frequent overflow risks.',
    ],
    checks: [
      'Location is tenant-owned; if company-specific, it belongs to the selected active legal entity.',
      'Attendance and leave engines read the same timezone and holiday context.',
      'Inactive locations are hidden from new employee and position setup.',
    ],
    references: [
      'docs/plans/2026-06-08-hrms-location-foundation-hardening-closeout.md',
      'docs/events/event-catalog.md',
    ],
  },
  {
    id: 'departments-designations',
    title: 'Departments and designations',
    description:
      'Maintain organization units and job labels separately so reporting lines, permissions, and job structures do not drift.',
    category: 'Company setup',
    role: 'HR',
    path: '/departments',
    updated: '2026-06-12',
    icon: UsersRound,
    keywords: [
      'department',
      'designation',
      'job title',
      'organization unit',
      'reporting',
      'sap foundation object',
    ],
    steps: [
      'Create departments under the intended legal entity or parent department.',
      'Use object-specific actions such as Open department page, Add sub-department, and Delete department.',
      'Create designations as job labels and keep them reusable across positions where policy allows.',
      'Assign owner roles and validate that employee self-service users cannot mutate organization setup.',
    ],
    checks: [
      'Department hierarchy is tenant-scoped and cannot point to another tenant parent.',
      'Designation status controls availability during position and employee creation.',
      'Negative ABAC paths block ordinary employees from setup mutations.',
    ],
    references: [
      'docs/plans/2026-06-05-hrms-org-master-department-designation-closeout.md',
      'libs/abac/src/lib/abac-bootstrap.service.ts',
    ],
  },
  {
    id: 'positions-headcount',
    title: 'Positions, headcount, and hiring controls',
    description:
      'Use positions as the controlled seat for employee assignment, manager hierarchy, FTE, and approved headcount.',
    category: 'Workforce planning',
    role: 'HR',
    path: '/positions',
    updated: '2026-06-15',
    icon: ClipboardList,
    keywords: [
      'position',
      'headcount',
      'fte',
      'vacancy',
      'manager',
      'designation',
      'location validation',
    ],
    steps: [
      'Create the position under one legal entity, department, designation, and location.',
      'Set headcount, FTE, status, effective date, cost center, and manager position where applicable.',
      'Use a staffed position as the manager position when the reporting line already exists; headcount capacity only controls employee assignment.',
      'Assign employees only when the position has capacity and the employee job information matches the position foundation data.',
      'Route high-risk changes such as broad role assignment or position control overrides through role-governance approval.',
    ],
    checks: [
      'Headcount and location are validated by the backend, not only by hidden frontend options.',
      'Employee creation blocks inactive, over-capacity, or mismatched positions.',
      'Reports To selection is structural and remains available for active staffed positions.',
      'Position permissions are role-specific and not proven by tenant-admin-only testing.',
    ],
    references: [
      'docs/plans/2026-06-08-hrms-basic-readiness-status.md',
      'docs/events/event-catalog.md',
    ],
  },
  {
    id: 'employee-create',
    title: 'Add an employee with documents, education, and experience',
    description:
      'Create the employee master record after foundation setup, then attach identity, education, experience, and joining evidence.',
    category: 'Employee lifecycle',
    role: 'HR',
    path: '/employees/new',
    updated: '2026-07-24',
    icon: UserRoundPlus,
    keywords: [
      'employee',
      'document upload',
      'education',
      'experience',
      'employee code',
      'joining',
      'background',
    ],
    steps: [
      'Select legal entity, location, department, designation, position, employment type, joining date, and reporting manager.',
      'Let the backend generate the employee code from the selected company rule: tenant prefix, company code, joining year, and the company running serial.',
      'Open the employee Documents tab after creation to confirm tenant-admin or HR Specialist document access before uploading evidence.',
      'Upload identity, address, education, experience, and statutory evidence with document type and expiry where applicable; the current local smoke proves MinIO PUT/download for ID, education-certificate, and experience-letter evidence.',
      'Review background records and document statuses before triggering joining-kit generation.',
    ],
    checks: [
      'Employee code is not generated until the company context is known.',
      'Fresh tenant admins and HR specialists can read/manage employee documents after bootstrap because document actions are explicitly permissioned server-side.',
      'Employee users can read their own document list but cross-employee document reads and tenant upload attempts return 403.',
      'Education and experience entries support document evidence, not only text fields; HR Specialist create/update and Employee negative paths are proven through Gateway.',
      'Cross-tenant document storage and employee references are rejected server-side.',
    ],
    references: [
      'docs/plans/2026-06-08-hrms-foundation-employee-create-gate-closeout.md',
      'docs/plans/2026-06-08-hrms-employee-code-display-contract-closeout.md',
      'docs/plans/2026-06-13-employee-background-document-evidence-closeout.md',
    ],
  },
  {
    id: 'employee-directory',
    title: 'Find and filter employees across the full directory',
    description:
      'Browse the complete permitted employee population with server-owned counts, bounded pages, readable filters, and search.',
    category: 'Employee lifecycle',
    role: 'HR',
    path: '/employees',
    updated: '2026-07-23',
    icon: UsersRound,
    keywords: [
      'employee directory',
      'employee search',
      'employee pagination',
      'rows per page',
      'employee filters',
      'cursor',
    ],
    steps: [
      'Open HRMS > Employees and use the total row count as the complete filtered population, not only the current page.',
      'Search by employee name, work email, or employee code, or filter by company, department, location, status, employment type, and joining-date range.',
      'Choose 10, 20, 30, 50, or 100 rows per page. Each change restarts at page one and requests a new bounded server page.',
      'Use Next and Previous to follow the server continuation cursor. Cursor pages intentionally do not offer a misleading jump to an unvisited last page.',
      'Open the employee row only after confirming the readable identity and organization context.',
    ],
    checks: [
      'The table total remains stable while moving through pages and matches the complete filtered count returned by HRMS.',
      'Search, filters, and page-size changes clear the previous continuation path before loading new rows.',
      'A record found by search must also be reachable through ordinary page navigation when filters are equivalent.',
      'Opaque page cursors are passed back unchanged and are never exposed as an operator input.',
    ],
    references: [
      'docs/plans/2026-07-23-hrms-employee-directory-pagination-closeout.md',
      'docs/docs/wiki/Employee-Lifecycle.md',
    ],
  },
  {
    id: 'employee-historical-migration',
    title: 'Migrate historical employees with a reviewed code plan',
    description:
      'Preview the complete company population, verify deterministic employee codes and reporting lines, then approve one guarded initial migration.',
    category: 'Employee lifecycle',
    role: 'HR',
    path: '/employees',
    updated: '2026-07-14',
    icon: FileCheck2,
    keywords: [
      'employee import',
      'historical migration',
      'initial migration',
      'employee code plan',
      'migration sequence',
      'profile photo',
      'bulk import',
    ],
    steps: [
      'Open Employees, choose Import Employees, and use Routine import only for normal incremental hires. Choose Initial migration for the first controlled load of an existing workforce.',
      'Download a fresh template and include the complete legal-entity population. Leave Employee Code blank so UniTree plans it; use Migration Sequence only to resolve employees with the same joining date.',
      'Set Platform Access to User account only when the worker should sign in. Use Record only for outsourced or administrative records that use placeholder contact details and must not receive an account, welcome email, role, or in-app notification.',
      'Keep the CSV or XLSX file within 500 rows and 5 MB. Use real calendar dates in YYYY-MM-DD or DD/MM/YYYY format, and ensure any supplied ID plus readable code/name resolve to the same active tenant record.',
      'Validate the file and review every row, including employee, joining date, company, planned code, current code, reporting manager, and any blocking error.',
      'Correct the source file when any row fails. Initial migration does not skip failed rows or partially apply a plan.',
      'Apply only the exact persisted plan that was reviewed. A changed file or employee-code sequence invalidates the plan and requires a new preview.',
      'Upload profile photos separately after the employee exists. Use JPG, PNG, or GIF up to 5 MB; do not place a local file path or image URL in the employee workbook.',
      'Keep sensitive data permission-scoped: plain PAN is identity metadata, Statutory PAN/UAN/PF/ESI require statutory-profile update access, and bank columns require both payment create and update access.',
    ],
    checks: [
      'Employee codes remain stable after migration: tenant prefix plus company code plus joining year plus a five-digit company serial that does not reset each year.',
      'Manager-first apply order does not change joining-date code order.',
      'Existing-employee reconciliation requires the dedicated migration and employee-code override permissions.',
      'Import permission alone never bypasses employee-document, payroll-payment, or statutory-profile permissions.',
      'The import-run ledger binds preview and apply to the tenant, source hash, complete plan, and sequence snapshot.',
      'Each successful row retains its normal transactional employee-created or employee-updated event.',
      'Mass profile-photo upload is a separate CSV-to-image mapping workflow and is not inferred from spreadsheet cells.',
      'Record-only creation stops before Auth provisioning. Giving an existing record platform access is a separate User & Security lifecycle decision, not a generic employee edit.',
    ],
    references: [
      'docs/plans/2026-07-14-historical-employee-import.md',
      'docs/docs/wiki/Employee-Lifecycle.md',
      'docs/events/event-catalog.md',
    ],
  },
  {
    id: 'attendance-regularization-on-behalf',
    title: 'Regularize attendance for an employee',
    description:
      'Submit a traceable attendance correction for yourself or an eligible employee without bypassing approval or scope controls.',
    category: 'Attendance',
    role: 'HR',
    path: '/attendance/regularization',
    updated: '2026-07-23',
    icon: ClipboardList,
    keywords: [
      'regularization',
      'regularize on behalf',
      'attendance correction',
      'missing check in',
      'missing check out',
      'manager scope',
      'department scope',
      'approval route',
      'attendance administrator',
      'governance approver',
      'configuration required',
      'record only employee',
    ],
    steps: [
      'Open Attendance > Regularization. Create your own correction, or choose Create for an employee when your assigned role and scope allow on-behalf work.',
      'Search the eligible employee picker by name, employee code, or work email. The list is limited server-side to your team, department, or tenant permission scope.',
      "Choose a date from the selected employee calendar, select the correction type, and enter the corrected time values when required. The control uses the affected employee's location timezone with tenant fallback, and shows its UTC offset plus stored UTC preview instead of relying on the browser timezone.",
      'If no attendance record exists for the day, UniTree locks the request to Mark as Present and explains that the attendance record changes only after approval. Other punch corrections require an existing record; a calendar loading error blocks submission.',
      'Keep corrected check-in on the attendance work date. Corrected check-out may use that date or the following date for a cross-midnight shift, must follow check-in, and cannot make the pair exceed 24 hours.',
      'For an on-behalf request, add an operator note that explains why the employee could not submit it. UniTree records both the affected employee and requesting account.',
      'Review the displayed approval route before submitting. UniTree resolves the reporting manager first, the optional Attendance Administrator policy step second, and the Attendance Governance Approver only for an exception.',
      'Submit and track the route in the request detail. On-behalf submission never approves the correction or changes the attendance record immediately.',
      'Approvers open the Approvals tab. Only a user assigned to the current route step can approve or reject, even when another user has broad tenant permission.',
      "An active, date-bounded Attendance Correction delegate also sees the original approver's assigned work. The decision records both people; delegation does not grant tenant-wide approval access.",
    ],
    checks: [
      'The self route derives the employee from the signed-in identity and never trusts a target employee ID from the browser.',
      'Team, department, and tenant eligibility are enforced by the attendance service after Gateway permission admission.',
      'The request creator and affected employee cannot decide the request. Duplicate manager/administrator steps collapse with a visible audited reason.',
      'If no independent approver exists, the request remains Configuration Required. It is never self-approved or automatically approved.',
      'Audit and event evidence retain submission mode, requesting auth user, affected employee, route stages, decisions, notes, timestamps, and rejection reason.',
      'A record-only employee can be selected by an authorized operator, but notification delivery is skipped when no linked platform account exists.',
      'HR Specialist does not receive tenant-wide attendance operation by default; Attendance Administrator owns daily time operation and tenant admin retains emergency setup access.',
      'If the current approver becomes inactive, leaves the reporting chain, or loses approval permission, decisions stop and the request shows Approval route needs recovery.',
      "The attendance service repeats work-date, cross-midnight, ordering, and 24-hour validation in the affected employee's resolved location timezone, with tenant fallback; changing browser values cannot bypass it.",
    ],
    references: [
      'docs/plans/2026-07-22-attendance-regularization-approval-governance.md',
      'docs/plans/2026-07-22-attendance-approval-route-recovery.md',
      'docs/plans/2026-07-22-regularization-date-time-control.md',
      'docs/docs/wiki/Time-Leave-and-Shifts.md',
      'docs/events/event-catalog.md',
    ],
  },
  {
    id: 'attendance-regularization-governance',
    title: 'Configure regularization approval ownership',
    description:
      'Prepare independent attendance decision makers before enabling policy-driven administrator review.',
    category: 'Attendance',
    role: 'Tenant admin',
    path: '/settings/users',
    updated: '2026-07-23',
    icon: ShieldCheck,
    keywords: [
      'attendance administrator',
      'attendance governance approver',
      'regularization policy',
      'manager approval',
      'four eye principle',
      'configuration required',
      'reroute',
      'route recovery',
      'delegation',
    ],
    steps: [
      'Open Settings > Users & Security > Users and choose Invite user when the independent approver does not need an employee record. Enter the accountable name and a unique sign-in email. UniTree creates a basic user account, sends the activation invitation, and does not create an employee code, job, reporting, attendance, or payroll record.',
      'After account creation, the Manage Roles dialog opens. Assign Attendance Governance Approver at tenant scope and complete the existing role-governance approval when prompted. Do not add Employee, People Manager, or broad administrator roles to this exception account.',
      'The invited approver activates the account and completes required MFA. Its dashboard shows Attendance Governance Approver, Assigned governance, and Open Assigned Approvals without pretending that an employee position, department, or profile exists.',
      'Open Settings > Users & Security > Users and assign Attendance Administrator to the person responsible for tenant-wide attendance operations.',
      'Open Attendance > Policies, edit the applicable policy, and enable Attendance Administrator Review only when the extra policy step is required. UniTree refuses this setting until an active Governance Approver exists.',
      'Keep reporting lines current because the employee manager is the normal first approver. Attendance-exempt executives remain a policy or employee-configuration decision, not an approval bypass.',
      'Open Attendance > Regularization > Approvals for daily work. Loading this worklist revalidates pending approval routes. Use the request combobox and Needs reassignment filter to isolate stalled work. Configuration Required items explain missing setup; Approval route needs recovery identifies a persisted approver who is no longer eligible.',
      'After correcting the user, role, delegation, or reporting setup, an Attendance Administrator or Tenant Administrator opens the request, chooses Recover Approval Route, and records the recovery reason.',
    ],
    checks: [
      'Attendance Governance Approver has no People Manager inheritance and no employee-directory mutation access.',
      'The invited user remains visibly unlinked in Users & Security. Link an employee profile later only when the person actually joins the workforce.',
      'Governance sees only requests assigned to its current route step and cannot browse or reroute unassigned blocked work.',
      'The governance-only sidebar omits employee self-service and Reports. Direct Reports access also requires hrms:reports:read:tenant and is denied without that explicit permission.',
      'Tenant-wide permission alone never makes a user the current request approver.',
      'An administrator who created an on-behalf request cannot approve it; Governance receives the exception only when independently eligible.',
      'Role expiration, revocation, tenant ownership, active-user status, and MFA policy remain enforced.',
      'The privileged role requires MFA before a token-bearing governance session can reach the application.',
      'A healthy route cannot be rerouted. Recovery preserves completed decisions and the prior route, and emits a dedicated auditable system event.',
      'For Charu Group, Sumit Gilhotra is the recommended Governance Approver after staging proof; Atul Gupta attendance exemption is separate production data configuration.',
    ],
    references: [
      'docs/plans/2026-07-22-attendance-regularization-approval-governance.md',
      'docs/plans/2026-07-22-attendance-approval-route-recovery.md',
      'docs/docs/wiki/Role-Journeys.md',
      'docs/events/event-catalog.md',
    ],
  },
  {
    id: 'attendance-overtime-approval',
    title: 'Request and approve planned overtime',
    description:
      'Submit planned overtime through a persisted Manager, Governance replacement, and optional independent Attendance Administrator route.',
    category: 'Attendance',
    role: 'Employee',
    path: '/attendance/overtime',
    updated: '2026-07-28',
    icon: ClipboardList,
    keywords: [
      'overtime',
      'planned overtime',
      'manager approval',
      'attendance administrator',
      'attendance governance approver',
      'approval setup required',
      'top hierarchy',
      'approval worklist',
      'delegate',
      'overnight work',
      'four eyes',
      'policy snapshot',
      'route recovery',
      'reroute',
      'inactive approver',
    ],
    steps: [
      'Open Attendance > Overtime > Requests and choose New request. Select the work date, enter the planned start and end, and give the business reason.',
      'Confirm the displayed employee timezone and stored UTC preview. The start stays on the work date; an overnight end may use the next date; the total cannot exceed 24 hours.',
      'Attendance Administrators configure coverage under Attendance > Overtime > Policies. Leave department and designation scopes empty for tenant-wide coverage, or use their searchable selectors to include matching departments or designations; explicit employee exclusions always win.',
      'Submit the request. UniTree resolves the first eligible reporting manager and applicable policy on the server; a browser-supplied manager ID or approval level cannot reroute it. If no policy applies, the form keeps your entries and tells you to contact an Attendance Administrator for policy setup.',
      'The assigned manager or an active overtime delegate opens Pending approvals and approves or rejects the Manager stage. For a top-hierarchy employee without an eligible manager, UniTree records Manager as skipped and assigns an independent Attendance Governance Approver instead.',
      'When the submission-time policy requires another review, the request changes to Awaiting next approval and an independent Attendance Administrator receives the next-stage notification. Governance may replace an unavailable administrator only when it did not already replace the manager.',
      'Track skipped stages, current assignees, actual decision actors, comments, timestamps, configuration blocks, and final status in request details. The employee receives approval only after the final required decision.',
      'If the request shows Approval setup required or Approval route needs recovery, an Attendance Administrator or Tenant Administrator opens the separate Route recovery queue, corrects the named reporting, user, role, or permission setup, and chooses Recover approval route with an operational reason. The owner may still withdraw blocked work instead.',
    ],
    checks: [
      'A missing eligible reporting manager uses the request-scoped Attendance Governance Approver replacement. If no independent Governance actor exists, the retained request shows Approval setup required and the exact configuration reason.',
      'A required second review needs an active Attendance Administrator distinct from the creator, employee, manager or Governance replacement, and prior decision makers. If Governance already replaced the manager, it cannot also replace that second stage.',
      'Policy scope is server-enforced. Department and designation selections use inclusive matching, an empty pair is tenant-wide, and explicit employee exclusions take precedence.',
      'Policy department, designation, and employee references must belong to the current tenant. Leaving comp-off expiry blank preserves a no-expiry policy.',
      'The applicable policy ID, complete route, current assignees, creator, subject, and administrator-review requirement are frozen on submission; later policy, hierarchy, or role edits do not silently rewrite the route.',
      'The request creator and subject cannot decide the request, and an actor who performs one decision cannot perform a later decision.',
      'Each decision uses the persisted current status, so stale or repeated actions fail without duplicate events or notifications.',
      'Any decision that advances to another stage sends an approval-pending message naming Manager, Governance, or Attendance Administrator; only final approval sends the employee an approved message.',
      'Governance Approvers see only requests whose current persisted stage assigns them. Their auth-only, MFA-protected role does not grant employee, policy, report, directory, or role-administration access.',
      'Recovery is available only through attendance:overtime-request:reroute:tenant. It preserves completed decisions, records the operator name, timestamp, reason, prior health reason, and outcome, and emits a transactional recovery event.',
      'A healthy route cannot be recovered. An unchanged blocked setup or stale concurrent recovery returns a conflict without another event.',
      'A request owner may withdraw Pending or Approval setup required work, but cannot edit route UUIDs or manually advance a blocked step.',
      'The employee filter is omitted for self-only users and uses a hierarchy-scoped directory for managers, preventing unauthorized tenant-directory requests.',
      'Create planned overtime only from Attendance > Overtime or `POST /api/attendance/overtime/requests`. The duplicate advanced create route is retired and returns `410 Gone`; retained legacy rows remain available only for controlled completion.',
    ],
    references: [
      'docs/plans/2026-07-23-attendance-overtime-approval-governance.md',
      'docs/plans/2026-07-23-attendance-overtime-preapproval-consolidation.md',
      'docs/plans/2026-07-28-attendance-overtime-route-recovery.md',
      'docs/docs/wiki/Time-Leave-and-Shifts.md',
      'docs/events/event-catalog.md',
    ],
  },
  {
    id: 'attendance-compensatory-time',
    title: 'Review and correct compensatory time',
    description:
      'Read earned time-off credits in days, identify their overtime source and expiry, and cancel an unused credit with a durable audit correction.',
    category: 'Attendance',
    role: 'Employee',
    path: '/attendance/overtime',
    updated: '2026-07-23',
    icon: FileCheck2,
    keywords: [
      'compensatory time',
      'comp off',
      'time off in lieu',
      'overtime credit',
      'expiry',
      'balance',
      'cancellation',
      'attendance administrator',
    ],
    steps: [
      'Open Attendance > Overtime > Comp-Off. The balance summary names its employee scope and records every quantity in days.',
      'Use the employee combobox only when your role has team, department, or tenant scope. Search by name, employee code, work email, or department; self-only users do not receive a tenant directory.',
      'Use the status selector or Available, Used, and Expired summary controls to filter the server-owned record list. Each row identifies the employee, source overtime date and hours, earned date, expiry, and lifecycle state.',
      'An employee with self-management permission or an Attendance Administrator with tenant management permission may choose Cancel credit on an active unused record. Enter a specific reason and confirm the destructive action.',
      'Cancellation keeps the Attendance record for audit, emits a transactional correction event, and makes HRMS reverse the original COMP_OFF credit through a durable idempotent balance posting.',
    ],
    checks: [
      'Available means persisted ACTIVE and not past the expiry date. A credit remains usable through its expiry date and becomes effectively expired on the following calendar day.',
      'The overtime policy standard daily hours converts source hours to days; UniTree does not assume every employee has an eight-hour workday.',
      'The use route takes the Comp-Off ID from the URL only. Clients do not send a duplicate body ID that can disagree with the route.',
      'Every employee target is validated against the signed-in tenant before summary, list, detail, use, or cancellation data is returned.',
      'Cancellation waits for the original HRMS credit posting, reverses exactly what HRMS posted, preserves actor and reason, and retries safely when event ordering is delayed.',
      'Payout versus time-off selection, partial allocation of one credit across leave requests, and two-decimal leave-ledger precision remain explicit follow-up decisions.',
    ],
    references: [
      'docs/plans/2026-07-23-attendance-comp-off-contract.md',
      'docs/docs/wiki/Time-Leave-and-Shifts.md',
      'docs/events/event-catalog.md',
    ],
  },
  {
    id: 'attendance-monthly-imports',
    title: 'Import monthly attendance safely',
    description:
      'Validate a CSV or XLSX source, resolve employees by readable codes, review conflicts, and apply eligible daily attendance through an audited asynchronous run.',
    category: 'Attendance',
    role: 'HR',
    path: '/attendance/imports',
    updated: '2026-08-11',
    icon: FileCheck2,
    keywords: [
      'attendance import',
      'bulk attendance',
      'monthly attendance',
      'biometric',
      'punching code',
      'employee code',
      'csv',
      'xlsx',
      'attendance administrator',
      'import profile',
      'error csv',
      'raw punches',
      'conflict replacement',
      'attendance governance approver',
    ],
    steps: [
      'Assign the MFA-protected Attendance Administrator role to the accountable operator. Read-only users may review run history, but only the import permission may inspect, validate, apply, or retry files.',
      'Open Attendance > Imports and download the Daily summary template. Use one row per employee and work date with Employee Code or Punching Code, Attendance Date, Check In, and Check Out. Do not use internal UUIDs.',
      'Choose the attendance month and the source IANA timezone. Times without an explicit offset are interpreted in that timezone; an earlier check-out is treated as next-day only for a valid overnight pair.',
      'Upload a CSV or XLSX file, choose an optional reusable vendor mapping profile, and select Inspect file. Confirm every source-column mapping before validation.',
      'Record an operational reason identifying the source, month, and purpose, then choose Validate rows. Validation creates a retained run and does not change attendance.',
      'Review Ready, Warning, Duplicate, Conflict, and Blocked counts. Open row details and download Error CSV when data or configuration needs correction.',
      'For a daily-summary run, choose Apply validated attendance, review the candidate count, and record the final apply reason. UniTree queues eligible rows; the page polls until the run completes.',
      'Use Run history to monitor, filter, inspect, download errors, or retry only failed eligible rows. The operator receives one in-app summary and, when an email is maintained, one reusable attendance email.',
      'For a Daily Summary row that differs from existing attendance, compare Existing and Uploaded values, select only the rows that should change, and choose Request uploaded values. Unselected rows keep existing attendance.',
      'An independent, MFA-protected Attendance Governance Approver opens Reviews and approves or rejects the assigned request with a mandatory comment. The requester cannot decide their own request.',
      'Use Raw punches only to validate and retain source evidence. Applying raw events remains unavailable until the shared pairing and valuation engine owns that calculation.',
    ],
    checks: [
      'Employee Code and Punching Code are tenant-scoped and must resolve to the same employee when both are supplied. Internal employee UUIDs are not accepted in the file.',
      'Rows outside the selected month, outside employee lifecycle, without an effective roster or shift, or inside locked timesheet or closed payroll periods are blocked.',
      'Approved leave or floating holiday, pending or approved regularization, cancelled or on-leave roster status, and differing existing attendance produce explicit conflicts instead of silent overwrite.',
      'Only a differing existing Attendance record is replaceable. Leave, regularization, period-lock, lifecycle, and setup conflicts remain blocked and cannot be overridden from the import page.',
      'Replacement defaults to keep existing, snapshots existing and uploaded values, requires an independent governance decision, and rechecks the current record and all mutable controls before each asynchronous replacement.',
      'Identical attendance and repeated normalized rows are marked duplicate and never applied. Spreadsheet status and total-hour columns do not control the result; UniTree derives hours from times and the effective shift break.',
      'Apply rechecks employee lifecycle, location, leave, floating holiday, regularization, locks, existing attendance, effective shift source, roster status, and break valuation inside each row transaction.',
      'Each applied row writes attendance, correction evidence, and a transactional event together. Shared attendance evaluation consumes the event durably; import completion alone does not finalize payroll.',
      'The source checksum, headers, mapping, timezone, actor, reason, normalized row evidence, result, and retry history remain available without showing internal IDs in operator messages.',
      'Employees do not receive one message per imported day. Only the accountable operator receives a completion or failure summary.',
    ],
    references: [
      'docs/plans/2026-08-04-attendance-import-runs.md',
      'docs/docs/wiki/Attendance-Imports.md',
      'docs/events/event-catalog.md',
      'README.md',
    ],
  },
  {
    id: 'attendance-timesheet-approvals',
    title: 'Review and decide scoped timesheets',
    description:
      'Use one approval worklist whose employees, departments, and decisions follow the same server-resolved authority.',
    category: 'Attendance',
    role: 'Manager',
    path: '/attendance/timesheet/approvals',
    updated: '2026-07-27',
    icon: FileCheck2,
    keywords: [
      'timesheet',
      'approval',
      'people manager',
      'department head',
      'attendance administrator',
      'delegation',
      'team scope',
      'department scope',
      'tenant scope',
      'concurrent decision',
    ],
    steps: [
      'Ask HR to maintain the formal reporting, team-lead, department-head, Attendance Administrator, or time-limited Timesheet delegation assignment. A title alone does not grant approval authority.',
      'Use Attendance > Timesheet for your own entry and the read-only Team Timesheets review tab. Use Attendance > Timesheet Approvals for decisions.',
      'Before submitting, complete or regularize every day marked Working that has no recorded hours. Leave, holiday, weekly-off, weekend, and absence entries may remain zero-hour non-working days.',
      'Use notes only for explanation. UniTree stores the day type separately on the server, so typing ABSENT, LEAVE, or HOLIDAY cannot change a Working day or bypass validation.',
      'Search the approval queue by employee name or code, then filter only by departments represented in your current approval population.',
      'Open a submitted timesheet, review every day and hour, then Approve or enter a specific rejection reason.',
      'Reopen a rejected timesheet, review the reason, correct its entries, and resubmit. Resubmission clears the current rejection fields and starts a new current decision.',
      'Refresh the queue when another approver acted first; UniTree preserves the first decision instead of overwriting it.',
    ],
    checks: [
      'People Manager and Team Lead approval stays inside the formally resolved team population; Department Head stays inside the managed department.',
      'A tenant approver may decide for other active tenant employees but cannot approve their own timesheet.',
      'A Timesheet delegation adds only the delegating manager’s population and only while that entity-specific delegation is active.',
      'The Team Timesheets read surface does not expose decision controls. Approval actions are available only from the approval-scoped worklist.',
      'A Working entry with zero regular and overtime hours is blocked in the browser and by the Attendance service; a crafted request cannot submit incomplete work evidence.',
      'Auto-populated Holiday entries use only active, non-optional holidays applicable to the employee location and department. Attendance sync clears stale system labels when a day becomes Working without deleting employee-entered notes.',
      'An entry update must belong to the selected tenant-scoped timesheet, and a newly added entry must remain inside that timesheet period.',
      'Changing an employee, department, tenant, or timesheet ID in a crafted request cannot escape the same server-owned target population.',
      'A stale second approval or rejection returns a conflict and requires a queue refresh.',
    ],
    references: [
      'docs/plans/2026-07-24-attendance-timesheet-approval-scope-closeout.md',
      'docs/docs/wiki/Time-Leave-and-Shifts.md',
      'README.md',
    ],
  },
  {
    id: 'manager-target-population',
    title: 'Use manager-scoped workforce pages',
    description:
      'Review employees, attendance, timesheets, leave, and shifts inside the population granted by formal organization assignments.',
    category: 'Manager self-service',
    role: 'Manager',
    path: '/employees/my-team',
    updated: '2026-07-20',
    icon: UsersRound,
    keywords: [
      'people manager',
      'team lead',
      'department head',
      'my team',
      'direct reports',
      'managed employees',
      'target population',
      'team timesheets',
      'attendance dashboard',
      'sales role',
      'crm access',
      'permissions',
    ],
    steps: [
      'Ask HR to maintain the formal reporting manager, team lead, or department-head assignment. A designation or position title alone does not grant manager access.',
      'Sign in with the intended manager account and review My Work Context on Dashboard. The scope badge explains whether access follows direct reports, led teams, or department leadership.',
      'Open HRMS > My Team to search the server-resolved population by name, employee code, department, or designation.',
      'Use Attendance > Team, Team Timesheets, approvals, leave requests, and other visible manager actions. Department filters contain only departments represented in the authorized population.',
      'Use My Shift Roster for the signed-in employee. Shift policy, coverage, template administration, tenant attendance reports, and tenant workforce statistics remain hidden unless a separate operator permission is assigned.',
    ],
    checks: [
      'People Manager resolves active formal direct reports only; indirect reports are not included.',
      'Team Lead resolves active members of teams the employee formally leads; title text does not create the role.',
      'Department Head resolves active employees in the formally managed department.',
      'Tenant Administrator, Manager, and Department Head assignments do not grant CRM access. CRM navigation and records require an explicit CRM Administrator, Sales Representative, Sales Manager, Sales Director, or custom CRM role.',
      'Dashboard does not request tenant HRMS statistics for a manager-only account, and Attendance does not request tenant report/register endpoints without tenant-report permission.',
      'Overtime does not request or render policy administration without `attendance:overtime-policy:read:tenant`.',
      'Changing an employee or department ID in a crafted request cannot escape the server-resolved target population or tenant boundary.',
    ],
    references: [
      'docs/plans/2026-07-20-people-manager-access-hardening.md',
      'docs/docs/wiki/Role-Journeys.md',
      'docs/docs/wiki/Time-Leave-and-Shifts.md',
    ],
  },
  {
    id: 'crm-administrator-access',
    title: 'Assign tenant-wide CRM oversight',
    description:
      'Give an executive or CRM operator tenant-wide configuration, governance, reporting, integration, and data oversight without making them a salesperson or record owner.',
    category: 'Identity and access',
    role: 'CRM operator',
    path: '/settings/users',
    updated: '2026-07-28',
    icon: ShieldCheck,
    keywords: [
      'crm administrator',
      'crm admin',
      'tenant administrator',
      'sales representative',
      'sales manager',
      'sales director',
      'crm access',
      'record owner',
      'role approval',
      'mfa',
      'governance',
    ],
    steps: [
      'Open Users & Security > Users, find the intended executive or CRM operator by name or email, and choose Manage roles.',
      'Select CRM Administrator. The server marks the role Approval required, explains its tenant-wide effect, and fixes the scope to Tenant.',
      'Enter the business reason and submit the governance request. Use an independent approver when one exists; the audited sole-admin path remains available only when no independent approver exists.',
      'Complete required MFA for the assignee, then verify CRM navigation, settings, integrations, imports/exports, analytics, reports, and tenant-wide record access.',
      'Assign Sales Representative, Sales Manager, or Sales Director separately only when the person must perform day-to-day sales work or own CRM records.',
    ],
    checks: [
      'Tenant Administrator alone still has no CRM access; combining it with CRM Administrator is explicit and auditable.',
      'CRM Administrator is tenant scoped, MFA protected, and never eligible for lead, task, deal, or other salesperson-owner selectors or automated assignment.',
      'Every CRM owner field is a server-filtered searchable combobox that intersects the acting user scope with active Sales Representative, Sales Manager, and Sales Director assignments; a failed lookup disables selection instead of exposing the general employee directory.',
      'The role assignment UI presents one valid primary action: direct assignment, governed request, or no action when unavailable.',
      'A tenant role administrator may request the exact canonical CRM Administrator role without inheriting its CRM permissions; custom-role max-privilege containment remains unchanged.',
      'Large combined Tenant Administrator and CRM Administrator permission sets use server-side permission-cache hydration instead of overflowing proxy headers.',
    ],
    references: [
      'docs/plans/2026-07-28-crm-administrator-role.md',
      'docs/docs/wiki/Role-Journeys.md',
      'docs/docs/wiki/Identity-Security-and-Access.md',
    ],
  },
  {
    id: 'crm-operating-units',
    title: 'Set up CRM Operating Units',
    description:
      'Create CRM-owned record and access boundaries, bind them to industry configuration, and manage explicit employee memberships without changing HR legal-employer data.',
    category: 'CRM access and routing',
    role: 'CRM Administrator',
    path: '/crm/settings/business-units',
    updated: '2026-08-04',
    icon: Building2,
    keywords: [
      'crm operating unit',
      'business unit',
      'legal entity',
      'industry line',
      'membership',
      'member reassignment',
      'default unit',
      'crm administrator',
      'record ownership',
      'access boundary',
    ],
    steps: [
      'Confirm the CRM Administrator role is active and MFA is complete. Tenant Administrator or a Sales role alone cannot manage operating units.',
      'Open CRM > Settings > Operating Units. Keep the tenant-wide default unit for shared CRM records; it has implicit tenant access and no editable member list.',
      'Choose New operating unit, enter its business-facing name, and bind it to an existing industry line or create a new line. Apply a starter pack only when that industry needs fields, statuses, or hierarchy labels.',
      'Select initial members with active Sales roles. You may later open Members to add employees or import selected employees from an HRMS company; the import materializes CRM membership and does not make HR legal entity the live security source.',
      'Before removing a member who owns records, choose another eligible active member in the same unit. Review lead, deal, task, activity, case, and other ownership before confirming.',
      'Rename or deactivate a non-default unit only after reviewing its counts. Transfer or archive every owned CRM record before deactivation.',
    ],
    checks: [
      'HRMS Legal Entity identifies the registered employer and statutory/payroll context; CRM Operating Unit identifies CRM record ownership and access. Changing either does not silently rewrite the other.',
      'Each CRM record remains stamped to one operating unit. Active, non-revoked membership is required where unit enforcement applies.',
      'The tenant-wide default unit cannot add, import, or revoke members and cannot be deactivated.',
      'A second unit on the same industry line requires explicit confirmation and an audited reason; the server enforces the rule even if the UI is bypassed.',
      'Removing the last active member is blocked while the unit owns records. Reassignment targets must be active, tenant-owned, and eligible in the same unit.',
      'Operating-unit enforcement is released progressively. Operators must review shadow telemetry before changing an environment to enforce mode.',
    ],
    references: [
      'docs/docs/wiki/CRM-Operating-Units.md',
      'docs/plans/2026-07-28-adr-business-units-v2.md',
      'docs/plans/2026-07-30-bu-v2-phase3-design.md',
      'docs/architecture/crm-scope-access.md',
    ],
  },
  {
    id: 'crm-sales-target-populations',
    title: 'Configure CRM Sales target populations',
    description:
      'Assign Sales roles to an explicit business population and configure fail-closed automated lead routing without changing an employee home department.',
    category: 'CRM access and routing',
    role: 'CRM Administrator',
    path: '/crm/settings/general',
    updated: '2026-07-30',
    icon: UsersRound,
    keywords: [
      'sales representative',
      'sales manager',
      'sales director',
      'target population',
      'assignment scope',
      'lead assignment',
      'round robin',
      'load balanced',
      'department',
      'team',
      'business line',
      'analytics scope',
      'reports scope',
    ],
    steps: [
      'Open Users & Security > Users, find the salesperson by name or email, and choose Manage roles.',
      'Assign Sales Representative with Self, Sales Manager with a named Sales Department or Team, or Sales Director with a named Sales Department. The target can differ from the employee HR home department.',
      'Sign in with a separate CRM Administrator assignment, then open CRM > Settings > General > Lead assignment and choose Round robin or Load balanced only when pooled automatic routing is required.',
      'Choose eligible Sales roles and at least one named department or team. Add business lines when routing must stay inside a narrower product or operating boundary.',
      'Choose an explicit fallback only when the business process requires one, save, then verify the CRM dashboard, analytics, reports, owner selectors, and a test lead using each intended role.',
    ],
    checks: [
      'A permission defines the operation; the persisted role assignment defines the target population. The server intersects both on every scoped CRM request.',
      'Sales Representative sees own CRM data, Sales Manager sees the assigned managed population, Sales Director sees the explicit assigned department, and CRM Administrator sees tenant-wide oversight data.',
      'The role assignment target does not rewrite the employee HR department, position, manager, or team membership.',
      'Sales Representative, Sales Manager, and Sales Director cannot change tenant CRM settings unless the user also holds CRM Administrator.',
      'New tenants start with automatic pooled lead assignment disabled.',
      'An enabled pooled strategy cannot be saved without an active eligible department or team. Invalid or empty populations leave the lead unassigned with audit evidence and never expand to another department.',
      'CRM Administrator and Tenant Administrator are not eligible Sales owners unless a separate eligible Sales role is explicitly assigned.',
    ],
    references: [
      'docs/plans/2026-07-29-crm-assignment-scope-governance-closeout.md',
      'docs/architecture/crm-scope-access.md',
      'docs/docs/wiki/Role-Journeys.md',
      'docs/docs/wiki/Identity-Security-and-Access.md',
    ],
  },
  {
    id: 'leave-time-off-notifications',
    title: 'Leave requests, balances, and encashment notifications',
    description:
      'Operate leave workflow events with durable notifications and payroll-owned encashment payout handoff.',
    category: 'Leave',
    role: 'HR',
    path: '/leaves/requests',
    updated: '2026-06-13',
    icon: FileText,
    keywords: [
      'leave',
      'time off',
      'leave balance',
      'carry forward',
      'expiry',
      'encashment',
      'notification',
      'event governance',
      'attachments',
      'leave attachment',
      'approval',
      'holiday calendar',
      'optional holiday',
      'inactive holiday',
      'holiday policy',
      'weekend pattern',
      'comp off',
      'holiday import',
      'holiday export',
      'location-scoped holiday',
      'shift holiday',
      'overtime holiday',
      'attendance holiday',
      'shift roster',
      'roster lifecycle',
      'publish roster',
      'lock roster',
      'my shifts',
      'employee roster',
      'self schedule',
      'shift swap',
      'preferred employee',
      'swap approval',
      'temporary schedule',
      'shift premium',
      'SHIFT_PREMIUM',
      'night shift premium',
      'weekend premium',
      'holiday premium',
    ],
    steps: [
      'Configure leave types, accrual rules, carry-forward policy, expiry policy, and encashment eligibility before employees submit requests.',
      'When creating or updating leave types through API/admin setup, send policy switches such as approval, attachment, paid leave, and proration as real booleans; the backend rejects string boolean values.',
      'Use `/leaves/types` to review active and inactive leave types during setup. Archive/remove keeps used leave types as inactive audit records and physically removes only unused setup rows.',
      'Configure holiday calendars separately from leave types. Use `/holidays` to review active and inactive calendar rows, and archive/remove used optional holidays so employee selections remain available for audit.',
      'Use `/holidays` Import for multiple calendar rows and Export to download the visible calendar. Location-scoped rows must reference active tenant locations before leave, attendance, or payroll consume them.',
      'Configure `/holidays/policy` with at least one unique weekend day. Weekend holiday comp-off expiry is stored only when the policy action grants compensatory off.',
      'Attendance roster generation, queued roster jobs, attendance working-day checks, and overtime premium calculation consume active non-optional holidays using company-wide plus matching employee location/department scope.',
      'Generate Schedules skips non-working holidays by default through `skipHolidays: true`; shifts are created on holidays only when the template is holiday-applicable or an explicit API/operator override sends `skipHolidays: false`.',
      'Publish, lock, unlock, or unpublish shift rosters only for active tenant employees and bounded planning windows; non-tenant, inactive, blank, duplicate, or too-wide employee/date requests fail before any schedule row is updated.',
      'Employees open `/shifts` as My Shifts. The UI requests only their own schedule rows, and the backend clamps self-service schedule reads to the authenticated employee profile.',
      'Use `/shifts/swap` for temporary shift swap, give-away, and cover requests. Preferred employee lists must resolve to active tenant employees, restricted postings are visible only to those employees, and location policy is enforced before a claim can be created.',
      'Managers review only pending swap claims. A manager cannot approve or deny a swap where they are the requester or claimer, even if their role has team, department, or tenant approval scope.',
      'When payroll runs, published or locked roster rows with active statuses, including reassigned `SWAPPED` rows, create `SHIFT_PREMIUM` earnings from configured night, weekend, and holiday allowance percentages without increasing overtime hours.',
      'Employees submit leave requests through HRMS; required supporting documents are uploaded as controlled leave-request attachments before approval.',
      'Managers and approvers review the request, approval progress, and attachment list from `/leaves/requests`; approval and rejection stay server-authorized by scope, delegation, and hierarchy.',
      'Approvers receive durable notification-service email and in-app prompts from `hrms.leave.requested`.',
      'Final approval, cancellation, and withdrawal update the attendance roster through the durable attendance listener and also notify affected users through notification-service.',
      'Leave balance accrual, carry-forward, expiry, and manual adjustment events now notify the linked employee through canonical `hrms.leave-balance.*` subjects.',
      'Leave encashment request, approval, and rejection events notify the manager or employee through canonical `hrms.leave-encashment.*` subjects.',
      'Approved unpaid encashments are picked up by payroll input resolution, posted as `LEAVE_ENCASHMENT` one-time earnings, linked to the payroll run and payslip, and closed only through payroll payment confirmation.',
      'Open `/leaves/encashment` to monitor approved encashments that are awaiting payroll run pickup, already linked to payroll but not yet paid, or paid through payroll payment confirmation.',
      'Use Event Governance at `/settings/system/events` to inspect producer, consumer, replay, and payroll handoff status for every leave event.',
    ],
    checks: [
      'Notification-service consumes leave request, leave balance, and encashment subjects durably with inbox idempotency.',
      'Leave request attachment upload/delete is owner-only and lifecycle-limited on the backend; read/download follows the parent leave request read scope.',
      'Leave balance notifications resolve the employee by tenant-owned employee record and require a linked auth user before sending.',
      'React email templates exist for accrued, carried-forward, expired, adjusted, requested, approved, rejected, cancelled, withdrawn, and encashment states.',
      'Approved leave encashment payout is payroll-owned: payroll input resolution filters approved, unlinked, unpaid encashments and payment confirmation marks linked encashments paid.',
      '`/leaves/encashment` shows payroll handoff badges for approved and paid requests, and the export includes the handoff state.',
      'Leave balance events do not create payroll or attendance projections by default; payroll uses approved encashments, and attendance uses approved/cancelled/withdrawn leave requests.',
      'Leave type setup is server-validated: boolean policy fields cannot be passed as strings or loose values.',
      'Leave type lifecycle is audit-safe: active request pickers stay active-only, while admin setup can include inactive records and used leave types are deactivated rather than physically deleted.',
      'Holiday calendar setup is audit-safe: `/holidays` requests inactive rows for setup review, and deleting a holiday with employee optional-holiday selections deactivates the holiday instead of cascading away selection history.',
      'Holiday import scope is server-validated: create, update, and bulk import reject non-tenant, inactive, blank, or duplicate location IDs; working-day, attendance roster, overtime, and payroll input resolution use only company-wide holidays when an employee has no location.',
      'Holiday policy setup is server-validated: weekend fallback patterns must contain one to seven unique days, and non-comp-off weekend holiday actions clear comp-off expiry.',
      'Shift roster generation respects the same holiday overlay scope: matching-location regional holidays and company-wide holidays are skipped by default, different-location regional holidays are ignored, employees without a location receive company-wide holidays only, and `skipHolidays: false` is an auditable override.',
      'Shift roster lifecycle setup is server-validated: publish, lock, unlock, and unpublish accept only active tenant employee IDs, reject duplicate or blank IDs, and cap each lifecycle request to 366 days.',
      'Shift roster self-service is owner-scoped: employee users receive `attendance:shift_schedule:read:self`, admin-only roster/template/assignment lookups stay hidden, and backend schedule reads reject missing or mismatched employee context.',
      'Shift swap setup is server-validated: preferred employees must be active tenant employees, restricted postings are filtered from non-preferred employees, no-location claimers cannot claim location-bound shifts unless cross-location swaps are enabled, and claim accept/reject/manager review actions are lifecycle-scoped.',
      'Payroll input resolution is schedule-owner scoped: if a swap reassigns a night, weekend, or holiday-applicable roster row, the new employee receives the premium record and the payroll result snapshot records the swapped schedule evidence.',
      'Frontend visibility is not the authority; attachments, leave balances, approval decisions, and tenant ownership remain enforced server-side.',
    ],
    references: [
      'docs/events/event-catalog.md',
      'docs/plans/2026-06-13-hrms-leave-balance-notifications-closeout.md',
      'docs/plans/2026-06-13-leave-encashment-payroll-event-alignment-closeout.md',
      'docs/plans/2026-06-13-leave-encashment-payroll-operator-report-closeout.md',
      'docs/plans/2026-06-13-leave-request-attachment-approval-scope-closeout.md',
      'docs/plans/2026-06-13-holiday-calendar-archive-closeout.md',
      'docs/plans/2026-06-13-holiday-policy-weekend-validation-closeout.md',
      'docs/plans/2026-06-13-holiday-import-location-scope-closeout.md',
      'docs/plans/2026-06-13-attendance-holiday-consumer-scope-closeout.md',
      'docs/plans/2026-06-13-shift-roster-lifecycle-validation-closeout.md',
      'docs/plans/2026-06-13-shift-roster-holiday-overlay-closeout.md',
      'docs/plans/2026-06-13-shift-swap-lifecycle-hardening-closeout.md',
      'docs/plans/2026-06-15-shift-swap-payroll-premium-closeout.md',
    ],
  },
  {
    id: 'hr-specialist-employee-detail-access',
    title: 'HR Specialist employee detail access',
    description:
      'Open employee detail as HR Specialist with tenant shell context and profile-image lookup, while payroll payment data stays denied.',
    category: 'Employee lifecycle',
    role: 'HR',
    path: '/employees/:id',
    updated: '2026-06-13',
    icon: UsersRound,
    keywords: [
      'hr specialist',
      'employee detail',
      'profile image',
      'tenant current',
      'app shell',
      'abac',
      'payment tab',
    ],
    steps: [
      'Assign HR Specialist through ABAC and run the current system-role sync for older tenants that were created before this policy update.',
      'Open the employee detail page; the app shell reads `/api/tenant/current` for tenant profile, logo, locale, and navigation context.',
      'Confirm the employee header/avatar calls `/api/hrms/employees/:id/profile-image-url` through the explicit employee-read route.',
      'Use Personal, Employment, Teams, Documents, Contact, Emergency, and System tabs for HR work; the Payment tab remains Payroll Manager-only.',
    ],
    checks: [
      'HR Specialist includes `tenant:profile:read:tenant`, `hrms:access:read:tenant`, and `hrms:employee:read:tenant`, but not `tenant:access:read:tenant`.',
      'Profile-image lookup uses the explicit employee-read Gateway route before the broad HRMS catch-all admission route.',
      'Employee self-service can read its own profile-image URL, while cross-employee profile-image lookup returns 403.',
      'HR Specialist employee detail renders on desktop and mobile without optional tenant/profile-image 403 calls, and without the Payment tab.',
    ],
    references: [
      'docs/plans/2026-06-13-hr-specialist-shell-profile-access-closeout.md',
      'docs/plans/2026-06-13-payroll-payment-information-role-closeout.md',
    ],
  },
  {
    id: 'joining-kit',
    title: 'Joining kit, templates, DocuSign, and evidence packages',
    description:
      'Generate PDF/DOC joining kit artifacts from templates, prefill employee data, collect signatures, and store audit evidence.',
    category: 'Documents',
    role: 'HR',
    path: '/onboarding/document-templates',
    updated: '2026-06-25',
    icon: FileCheck2,
    keywords: [
      'joining kit',
      'document template',
      'pdf',
      'docx',
      'prefill',
      'docusign',
      'signature',
      'audit package',
    ],
    steps: [
      'Create or select a document template with the correct company, role, assignment rule, fields, and signer placement.',
      'Use the typed field picker for employee, contact, identity, statutory, organization, payment, joining-kit profile, family/nominee/reference summaries, highest education including board/state/division/subjects, latest prior experience including reporting-to, salary/reason/responsibilities, total prior-experience, and same/other-industry experience placeholders.',
      'Open the employee detail page, complete the Joining kit tab, and keep education, prior experience, identity documents, uploaded documents, and payroll payment data in their own profile sections. Generated joining-kit records include full primary payment details when they are maintained.',
      'Generate the joining kit from the employee document tab after employee foundation data is complete.',
      'Download generated PDFs or send the signature envelope when the provider is configured.',
      'Use HR recovery actions to retry, void, or download provider evidence when callbacks or downloads fail.',
    ],
    checks: [
      'Default PDFs include joining-kit declarations, family/nominee/reference summaries, and education/prior-experience background sections when records exist.',
      'Generated PDFs use the registered legal entity name in uppercase as the document header, and repeatable particulars such as family, nominee, and references render one record per line.',
      'Template previews and generated PDFs resolve background placeholders and same/other-industry experience totals from tenant-owned employee records, not free-form text.',
      'Payment account, UPI, SWIFT, and IBAN values remain masked in payment APIs and audit logs, but generated joining-kit records render full maintained values for employee-record submission.',
      'Generated files are persisted as employee documents with template metadata and can be downloaded back through the document storage handoff.',
      'Employee self-service users cannot generate tenant joining kits; HR Specialist or tenant-admin generation is required.',
      'Signature placement survives template changes and uses anchor placement when available.',
      'Audit evidence package includes document metadata, signature task state, and provider evidence.',
    ],
    references: [
      'docs/plans/2026-06-13-joining-kit-template-generation-closeout.md',
      'docs/plans/2026-06-09-docusign-signer-placement-closeout.md',
      'docs/plans/2026-06-08-hrms-document-template-employee-preview-closeout.md',
    ],
  },
  {
    id: 'employee-payment-information',
    title: 'Manage employee payment information',
    description:
      'Maintain payroll-owned bank-transfer, UPI, wire, cheque, cash, or other payment records with masked sensitive values.',
    category: 'Payroll',
    role: 'HR',
    path: '/employees/:id',
    updated: '2026-06-13',
    icon: Landmark,
    keywords: [
      'payment information',
      'bank account',
      'payroll manager',
      'upi',
      'ifsc',
      'masked',
      'audit',
    ],
    steps: [
      'Assign the Payroll Manager role through ABAC before opening employee payment information.',
      'Open the employee detail page and select the Payment tab; HR Specialist and Employee users should not see this tab.',
      'Create the active primary bank-transfer record with effective date, account holder, bank, account number, IFSC, and country.',
      'Add secondary methods such as UPI only when method-specific identifiers are present.',
      'Deactivate stale payment methods instead of deleting historical payroll evidence.',
    ],
    checks: [
      'Gateway must hydrate permissions from persisted ABAC role assignments; embedded role names are not enough.',
      'Account number, UPI, SWIFT, and IBAN values are masked in API responses and audit logs.',
      'Backend validation rejects invalid date ranges, invalid IFSC format, and missing method-specific identifiers.',
      'Generated joining-kit records can include full primary payment details; normal payment API responses and audit logs continue to mask account, UPI, SWIFT, and IBAN values.',
    ],
    references: [
      'docs/plans/2026-06-13-payroll-payment-information-role-closeout.md',
      'docs/plans/2026-06-08-hrms-basic-readiness-status.md',
    ],
  },
  {
    id: 'security-mfa-passkeys',
    title: 'MFA, passkeys, sessions, and verification policy',
    description:
      'Configure identity assurance before inviting the wider workforce or moving from staging to production.',
    category: 'Security',
    role: 'Tenant admin',
    path: '/settings/auth',
    updated: '2026-07-13',
    icon: Fingerprint,
    keywords: [
      'mfa',
      'passkey',
      'webauthn',
      'totp enrollment',
      'mfa recovery',
      'platform administrator',
      'superadmin bootstrap',
      'passkey recovery',
      'session',
      'password policy',
      'sign-in cooldown',
      'account recovery',
      'notification inbox',
      'email verification',
      'phone verification',
    ],
    steps: [
      'Set password, session, and verification policy for the tenant.',
      'Keep phone verification disabled only when SMS DLT templates are not approved, and document the environment override.',
      'MFA and passkeys are available on every plan; keep them enabled for admins and sensitive HR/payroll/security roles.',
      'When a privileged user has not enrolled MFA, login stops at Set up MFA to continue. Scan the QR code in an authenticator app, use the setup key only from the "can’t scan" fallback, verify the current code, then save or download backup codes before the dashboard session opens.',
      'Platform administrators always use this gate. Operators create only the first platform account with the guarded `pnpm db:seed:superadmin` command; normal database seeding creates no account and has no default password.',
      'MFA activation sends a security confirmation email, but backup codes are shown once in the browser and are never included in email.',
      'Users add passkeys from Profile > Security > Passkeys. After password step-up, the browser opens the native WebAuthn prompt to save the passkey on the device, a synced password manager, a phone QR flow, or a hardware security key.',
      'For staging and production, keep `FRONTEND_URL` and `CORS_ORIGINS` aligned to the exact HTTPS app origin so passkeys bind to the expected relying-party domain.',
      'For lost authenticators, use Settings > Users > Disable MFA only after verifying the employee identity, recording the recovery reason, and completing password step-up. The user must re-enroll when policy requires MFA.',
      'For lost passkeys or security keys, use Settings > Users > Reset passkeys only after identity verification, recovery reason capture, and password step-up. The user must register a new passkey before passkey sign-in works again.',
      'For suspicious access, use Settings > Users > Sign out user only after recording the revocation reason and completing password step-up.',
      'For a user blocked by failed-sign-in policy, open Authentication > Security Operations or Users & Security > Users, choose Reset sign-in cooldown, verify identity, record a reason, and complete current-password step-up. Superadmins use the selected tenant User Recovery tab and search by readable account details.',
      'Users & Security shows the locale-formatted date with tenant-local time below it for the canonical Last sign-in after a token-bearing password, MFA, passkey, magic-link, or OAuth session completes. An invited user or a user who stops at required MFA correctly shows Never.',
      'A general Too many requests response is a Gateway traffic control, not a user cooldown. Keep the session, wait for the server-provided Retry-After period, and let the application perform its single jittered retry instead of repeatedly refreshing.',
      'Use the notification bell for recent activity or open Notifications from the app sidebar for the full All/Unread inbox. Each row menu can mark the item read, open its business action when available, or dismiss it; the inbox menu opens settings and bulk actions. Platform administrators receive a separate system-scoped inbox, while tenant users receive only their own tenant-scoped inbox.',
      'Admin factor recovery and active-session revocation emit auth.mfa.force_disabled, auth.passkey.force_reset, or auth.session.revoked events so notification-service can send React-rendered email and in-app alerts to the affected user.',
      'A cooldown reset emits auth.sign_in.cooldown_reset and preserves failed-attempt history behind a reset marker. It never resets shared IP controls or a service circuit breaker.',
      'Review role assignment approval for high-risk access so one admin cannot silently grant broad privileges.',
    ],
    checks: [
      'Email verification is enforced for signup and admin invite flows.',
      'MFA/passkey UI exists for end users, backend policy is the enforcement point, and privileged roles carry MFA-required metadata.',
      'Unenrolled privileged users receive a short-lived MFA enrollment challenge instead of access and refresh tokens.',
      'Platform password login never returns tokens before TOTP enrollment or verification succeeds.',
      'Passkey registration and login use the browser WebAuthn prompt; UniTree does not display an application-owned QR code for passkeys.',
      'Synced passkeys that return a zero signature counter remain valid without a clone warning; a supported counter that does not increase remains a security audit signal.',
      '`FRONTEND_URL` and `CORS_ORIGINS` include the browser origin used by employees before WebAuthn is enabled in staging or production.',
      'Tenant admin MFA recovery requires auth:mfa:force:tenant, password step-up, a non-empty reason, tenant-scoped backend validation, and immutable audit context.',
      'Tenant admin passkey recovery requires auth:passkey:force:tenant, password step-up, a non-empty reason, tenant-scoped backend validation, and immutable non-secret audit context.',
      'Tenant admin session revocation requires auth:session:revoke:tenant, password step-up, a non-empty reason, tenant-scoped backend validation, warning audit context, and affected-user notification when active sessions are revoked.',
      'Sign-in cooldown recovery requires auth:user:unlock:tenant, an active same-tenant user and cooldown, password step-up, a non-empty reason, audit/event evidence, and affected-user notification.',
      'Last sign-in is tenant-scoped, uses the canonical authentication profile, is updated only after a completed token-bearing session, and displays both locale date and tenant-local time.',
      'Gateway traffic limits identify authenticated callers by tenant and subject, use client IP for anonymous traffic and the emergency flood ceiling, return a structured 429 plus Retry-After, and do not alter user cooldown state.',
      'Notification list, unread count, read, dismiss, and preference operations require explicit gateway and notification-service self permissions. A role label alone never grants access.',
      'The Notifications sidebar entry is visible only to an eligible signed-in role; API authorization remains the enforcement point even when navigation is hidden.',
      'Recovery and session-revocation notification events are visible through Event Governance and use durable inbox consumption in notification-service.',
      'Role governance events and audit logs exist for sensitive permission changes.',
    ],
    references: ['apps/auth/docs/README.md', 'docs/events/event-catalog.md'],
  },
  {
    id: 'audit-log-investigation',
    title: 'Investigate an audit event',
    description:
      'Read actor, target, field changes, request context, and technical evidence in an operator-first investigation view.',
    category: 'Security',
    role: 'Tenant admin',
    path: '/settings/audit-logs',
    updated: '2026-07-14',
    icon: ShieldCheck,
    keywords: [
      'audit logs',
      'before after',
      'field changes',
      'actor',
      'target',
      'ip address',
      'correlation id',
      'request context',
    ],
    steps: [
      'Open Settings > Audit Logs and filter by date, service, action, status, actor, or affected resource.',
      'Open an event to review the readable event summary, actor, target, reason, outcome, and tenant context before technical identifiers.',
      'Use the field-change ledger to compare before and after values. Sensitive values remain masked even for authorized audit readers.',
      'Review request context for IP address, location, and client information, then expand Technical evidence only when a support or security investigation needs IDs, permissions, or raw metadata.',
      'Copy the correlation or resource identifier from Technical evidence when escalating the event to engineering or security operations.',
    ],
    checks: [
      'Audit access remains permission-scoped and tenant-scoped on the backend; a visible link is not authorization.',
      'Local IPv6 loopback `::1` is a valid local address. Historical `unknown` values cannot be reconstructed, and production proxy forwarding must be verified after deployment.',
      'The detail view is keyboard-operable and responsive without forcing raw UUIDs into the primary information hierarchy.',
      'Before/after data, reason, error, actor, target, and request context remain preserved as immutable evidence.',
    ],
    references: [
      'docs/plans/2026-07-14-workforce-operations-ux-closeout.md',
      'docs/docs/wiki/Notifications-Events-and-Audit.md',
    ],
  },
  {
    id: 'crm-campaign-email',
    title: 'Build and send an email campaign',
    description:
      'Create a campaign, add addressable recipients manually or from CSV, review the email, and understand sent, suppressed, failed, and engagement results.',
    category: 'CRM campaigns',
    role: 'CRM operator',
    path: '/crm/campaigns',
    updated: '2026-07-20',
    icon: Megaphone,
    keywords: [
      'crm',
      'campaign',
      'email',
      'recipients',
      'csv import',
      'frequency cap',
      'suppressed',
      'target group',
      'execution',
      'verified sender',
      'track opens',
      'track clicks',
    ],
    steps: [
      'Open `CRM > Campaigns`, create an email campaign, and open its detail workspace.',
      'Add existing leads or choose `Import CSV`. Campaign CSV rows require first name, last name, and an email address; phone-only leads remain valid CRM leads but are not addressable campaign recipients.',
      'When importing, download the current template, choose the duplicate policy, upload the file, and wait for the terminal import status. `Partial` means some rows or campaign enrollment failed; open the error list before continuing.',
      'Configure the campaign email with a verified From domain. Choose whether this campaign tracks opens and clicks; the outbox preserves those choices for delayed sends and retries.',
      'Review the recipient count and content, then start the campaign and use `Review & Send` for the deliberate send confirmation. Sending remains blocked when sender verification is unavailable or the saved domain is not verified.',
      'Read execution results separately: `Suppressed` means a policy, opt-out, suppression-list, or frequency-cap block; `Failed` means an actual send failure. Delivered, opened, clicked, bounced, and unsubscribed remain separate outcomes.',
    ],
    checks: [
      'The acting user needs import permission and effective update authority for the selected campaign; the backend rejects out-of-scope enrollment before it queues a job.',
      'Marketing and triggered email share the tenant frequency budget. Transactional CRM and test email are exempt.',
      'A completed import does not appear until campaign enrollment has finished. Enrollment failure is reported as `Partial`, not false success.',
      'Campaign execution status shows status, progress, total recipients, sent, suppressed, and failed values from the same API contract.',
      'The backend enforces the verified-sender rule for normal execution, scheduled journeys, resend, and test-send paths; a direct API call cannot bypass the UI state.',
      'Gmail image proxy and other ambiguous automatic fetches remain visible in raw outbound lifecycle evidence but do not count as human opens, change campaign-member engagement, score leads, or trigger journeys.',
      'Use a provider contract that permits the intended campaign volume and marketing purpose before production execution; successful transactional test delivery is not provider approval for bulk promotional mail.',
      'Use `Settings > System > Events` for governed `crm.message.blocked_by_cap` evidence; the suppressed outbound-message row remains the durable audit record.',
    ],
    references: [
      'docs/plans/2026-07-16-safe-cap-frequency-cap-spec.md',
      'docs/plans/2026-07-20-crm-campaign-deliverability-hardening.md',
      'docs/events/event-catalog.md',
      'docs/docs/wiki/CRM-Campaigns-and-Imports.md',
    ],
  },
  {
    id: 'developer-api-events',
    title: 'Developer API, events, and webhooks',
    description:
      'Use the gateway, Swagger, event catalog, and webhook contracts to integrate safely without bypassing tenant isolation.',
    category: 'Developer',
    role: 'Developer',
    path: '/api/docs',
    updated: '2026-06-12',
    icon: Braces,
    keywords: ['api', 'gateway', 'swagger', 'events', 'webhook', 'asyncapi', 'docusign callback'],
    steps: [
      'Use gateway routes as the public API boundary unless a service route is explicitly internal.',
      'Read Swagger for request and response contracts, then confirm event side effects in `docs/events/`.',
      'For webhooks, verify signatures, raw payload storage, idempotency, and replay behavior.',
      'Document any new event producer, consumer, replay path, and UI exposure in the same slice as the code.',
    ],
    checks: [
      'Gateway, controller, service, Prisma, events, notifications, docs, and tests are updated together.',
      'No API relies on frontend-only tenant, date, or lifecycle validation.',
      'New events have owner, replayability, and UI exposure notes.',
    ],
    references: ['docs/events/asyncapi.yaml', 'docs/events/event-catalog.md'],
  },
  {
    id: 'staging-deployment',
    title: 'Staging deployment and image transport',
    description:
      'Deploy staging from prebuilt ECR images without compiling multi-service Docker images on the shared runtime host.',
    category: 'Operations',
    role: 'Developer',
    path: 'https://staging.unitree.mvpl.info',
    updated: '2026-06-13',
    icon: Network,
    keywords: [
      'staging',
      'deployment',
      'docker',
      'ecr',
      'github actions',
      'runtime host',
      'ssh',
      'ssm',
      'environment variables',
      'configuration validation',
    ],
    steps: [
      'Build service images through GitHub Actions or a local/operator builder, not on the staging EC2 runtime host.',
      'Push images to the UniTree staging ECR repositories before restarting containers.',
      'For manual local builds, scope the service set with `UNITREE_STAGING_SERVICES="gateway auth tenant admin" ./docker/aws/deploy.sh build` and push the same scoped set.',
      'Deploy by copying compose/frontend assets, pulling ECR images on EC2, running required Prisma migrations, and restarting containers.',
      'Provide one canonical DATABASE_URL and REDIS_URL binding, or a complete discrete connection tuple. Do not duplicate credentials merely to satisfy validation.',
      'Record the commit SHA, build path, migration status, public health result, and browser QA evidence in the handoff and closeout note.',
    ],
    checks: [
      'No multi-service Docker build is running on the shared staging host.',
      'The staging health endpoint returns 200 after deploy: `https://staging.unitree.mvpl.info/api/health/ready`.',
      'Non-CRM service images install runtime dependencies from generated service package metadata and pruned service lockfiles.',
      'Staging and production fail closed when shared configuration is invalid, and the deployment rejects fresh startup logs containing configuration-validation errors.',
      'Docs-only and workflow-only pushes use `[skip ci]` or path filters so Actions minutes are not wasted.',
    ],
    references: [
      'docs/docs/wiki/Operations.md',
      'docker/aws/deploy.sh',
      '.github/workflows/ci.yml',
    ],
  },
  {
    id: 'superadmin-tenant-provisioning',
    title: 'Superadmin tenant provisioning',
    description:
      'Create or recover tenant shells from the platform side while keeping the first admin identity separate from employee data.',
    category: 'Platform admin',
    role: 'Superadmin',
    path: '/superadmin/tenants',
    updated: '2026-06-12',
    icon: KeyRound,
    keywords: [
      'superadmin',
      'tenant provisioning',
      'admin invite',
      'tenant shell',
      'status',
      'deactivate',
    ],
    steps: [
      'Create the tenant shell with company identity, slug, locale, timezone, plan, and first-admin invite.',
      'Confirm tenant-service provisioning paths such as `POST /api/tenants` write the same HRMS foundation pending marker and baseline MFA/passkey auth config.',
      'Use lifecycle actions for activate, suspend, deactivate, and invite recovery instead of editing status through generic forms.',
      'Confirm the tenant admin completes setup from the tenant side before employees are created.',
      'Use audit logs and tenant detail pages for operational recovery and evidence.',
    ],
    checks: [
      'Superadmin actions are separate from tenant-admin setup permissions.',
      'Tenant status transitions are explicit and audited.',
      'Provisioning does not create employee master data automatically.',
    ],
    references: [
      'docs/plans/2026-06-08-tenant-signup-admin-identity-public-docs-closeout.md',
      'docs/audits/services/auth.md',
    ],
  },
] as const satisfies readonly DocTopic[];

const apiReferences = [
  {
    method: 'GET',
    path: '/api/crm/business-units',
    owner: 'CRM operating-unit administration',
    purpose:
      'List tenant operating units with industry binding, active-member totals, and owned-record counts.',
  },
  {
    method: 'POST',
    path: '/api/crm/business-units/onboard',
    owner: 'CRM operating-unit administration',
    purpose:
      'Create or recover one operating unit together with its industry line, optional starter pack, and initial memberships.',
  },
  {
    method: 'POST',
    path: '/api/crm/business-units/:id/members/revoke',
    owner: 'CRM operating-unit membership',
    purpose:
      'Soft-revoke an active membership and require an eligible same-unit reassignment target when the member owns CRM records.',
  },
  {
    method: 'POST',
    path: '/api/crm/import/lead/presign',
    owner: 'CRM campaign recipient import',
    purpose: 'Create a tenant-scoped upload handoff for the reviewed campaign CSV template.',
  },
  {
    method: 'POST',
    path: '/api/crm/import/lead/start',
    owner: 'CRM campaign recipient import',
    purpose:
      'Validate campaign write scope, create or match addressable leads, enroll recipients, and preserve row or enrollment errors in the import job.',
  },
  {
    method: 'POST',
    path: '/api/crm/campaigns/:id/execute',
    owner: 'CRM campaign execution',
    purpose:
      'Run the reviewed campaign through compliance, frequency-cap reservation, suppression, and outbound queue controls.',
  },
  {
    method: 'GET',
    path: '/api/crm/campaigns/:id/execution-status',
    owner: 'CRM campaign execution',
    purpose:
      'Read status, progress, total members, sent, failed, and suppressed counts for the current execution.',
  },
  {
    method: 'POST',
    path: '/api/auth/tenant/signup',
    owner: 'Public tenant signup',
    purpose: 'Create tenant shell and first admin identity without employee master data.',
  },
  {
    method: 'POST',
    path: '/api/auth/superadmin/login',
    owner: 'Platform superadmin',
    purpose:
      'Verify the platform password and return a browser-bound TOTP enrollment or verification challenge without issuing tokens.',
  },
  {
    method: 'POST',
    path: '/api/auth/mfa/enrollment/setup',
    owner: 'Privileged MFA enrollment',
    purpose:
      'Return QR/manual-key TOTP setup data after validating the short-lived enrollment challenge.',
  },
  {
    method: 'POST',
    path: '/api/auth/mfa/enrollment/verify',
    owner: 'Privileged MFA enrollment',
    purpose:
      'Activate TOTP, return one-time backup codes, consume the challenge, and issue the dashboard session.',
  },
  {
    method: 'POST',
    path: '/api/auth/magic-link/request',
    owner: 'Passwordless sign-in request with cooldown controls',
    purpose: 'Start passwordless sign-in with rate limits, tenant checks, and email delivery.',
  },
  {
    method: 'GET',
    path: '/api/auth/admin/auth/tenants/:tenantId/users',
    owner: 'Tenant sign-in recovery selection',
    purpose:
      'List tenant-owned users with readable account and effective cooldown details for permitted tenant or platform administrators.',
  },
  {
    method: 'GET',
    path: '/api/auth/admin/auth/tenants/:tenantId/sign-in-cooldowns',
    owner: 'Tenant security operations',
    purpose: 'List only users whose tenant failed-sign-in policy cooldown is currently active.',
  },
  {
    method: 'POST',
    path: '/api/auth/admin/auth/tenants/:tenantId/users/:userId/sign-in-cooldown/reset',
    owner: 'Tenant user sign-in recovery',
    purpose:
      'Reset one active same-tenant user cooldown after password step-up and reason capture while preserving failed-attempt history.',
  },
  {
    method: 'GET',
    path: '/auth/magic-link/verify',
    owner: 'Email callback that verifies a single-use magic link token',
    purpose: 'Consume the magic-link token and exchange it for an authenticated session.',
  },
  {
    method: 'POST',
    path: '/api/superadmin/tenants',
    owner: 'Superadmin tenant provisioning',
    purpose: 'Create tenant shell and send first-admin invite from the platform surface.',
  },
  {
    method: 'POST',
    path: '/api/tenants',
    owner: 'Tenant-service provisioning',
    purpose:
      'Create tenant shell, first admin identity, HRMS foundation pending marker, and baseline auth security config.',
  },
  {
    method: 'GET',
    path: '/api/tenant/current',
    owner: 'App shell tenant profile',
    purpose:
      'Read tenant profile, logo, locale, and navigation context for authenticated tenant roles without granting tenant settings access.',
  },
  {
    method: 'GET',
    path: '/api/tenant/current/settings',
    owner: 'Tenant setup',
    purpose: 'Read tenant locale, timezone, currency, and verification policy.',
  },
  {
    method: 'POST',
    path: '/api/attendance/bulk/imports/inspect',
    owner: 'Attendance import inspection',
    purpose:
      'Parse a tenant operator CSV/XLSX source, return headers, a bounded sample, and suggested readable-code mappings without retaining or mutating attendance.',
  },
  {
    method: 'POST',
    path: '/api/attendance/bulk/imports/preview',
    owner: 'Attendance import validation',
    purpose:
      'Normalize rows, resolve employee and shift context, enforce lifecycle/leave/lock/conflict controls, and retain an auditable validation run without applying attendance.',
  },
  {
    method: 'GET',
    path: '/api/attendance/bulk/imports/runs',
    owner: 'Attendance import history',
    purpose:
      'Return bounded tenant-scoped run history by mode, month, and lifecycle status for Attendance Import readers.',
  },
  {
    method: 'GET',
    path: '/api/attendance/bulk/imports/runs/:runId',
    owner: 'Attendance import evidence',
    purpose:
      'Return one tenant-owned run plus paginated row validation and apply evidence; row status can be filtered without exposing another tenant.',
  },
  {
    method: 'POST',
    path: '/api/attendance/bulk/imports/runs/:runId/apply',
    owner: 'Attendance import apply',
    purpose:
      'Queue Ready and Warning daily-summary rows with operator reason, then revalidate every row inside its attendance correction transaction.',
  },
  {
    method: 'GET',
    path: '/api/attendance/bulk/imports/runs/:runId/errors',
    owner: 'Attendance import error report',
    purpose:
      'Download blocked, conflict, and failed row evidence as CSV using readable employee references and remediation messages.',
  },
  {
    method: 'GET',
    path: '/api/attendance/timesheet/approvals',
    owner: 'Timesheet approval worklist',
    purpose:
      'Return submitted or historical timesheets, pagination, and department facets from the current actor’s explicit team, department, tenant, or active Timesheet-delegation approval population.',
  },
  {
    method: 'POST',
    path: '/api/attendance/timesheet/:timesheetId/approve',
    owner: 'Timesheet approval decision',
    purpose:
      'Approve a submitted timesheet only when the employee is in the same server-resolved approval population; deny self, cross-tenant, unrelated, and stale concurrent decisions.',
  },
  {
    method: 'POST',
    path: '/api/attendance/timesheet/:timesheetId/reject',
    owner: 'Timesheet rejection decision',
    purpose:
      'Reject a submitted timesheet with a reason under the same scope and compare-and-set controls used by approval.',
  },
  {
    method: 'GET',
    path: '/api/attendance/regularization/approval-route-preview',
    owner: 'Attendance regularization',
    purpose:
      "Resolve the tenant-scoped manager, policy-driven administrator step, duplicate skips, Governance fallback, and affected employee's effective location timezone before submission.",
  },
  {
    method: 'GET',
    path: '/api/attendance/regularization/pending/approvals',
    owner: 'Attendance approval inbox',
    purpose:
      'Return requests assigned directly or through an active Attendance Correction delegation, plus unhealthy pending items for Attendance Administrator or Tenant Administrator recovery operators.',
  },
  {
    method: 'POST',
    path: '/api/attendance/regularization/:requestId/approve',
    owner: 'Attendance approval decision',
    purpose:
      'Approve only a healthy persisted current route step after creator, subject, direct-or-delegated assignment, permission, tenant, and status checks; delegated decisions retain both actors.',
  },
  {
    method: 'POST',
    path: '/api/attendance/regularization/:requestId/reroute',
    owner: 'Attendance approval recovery',
    purpose:
      'Require Attendance Administrator or Tenant Administrator to give a reason before recovering an unhealthy pending route, preserving completed decisions and the prior route; Governance cannot invoke it.',
  },
  {
    method: 'POST',
    path: '/api/attendance/overtime/requests',
    owner: 'Planned overtime submission',
    purpose:
      'Validate employee-local business time, resolve the manager and applicable policy, freeze the review requirement, and reject an incomplete or non-independent route before persistence.',
  },
  {
    method: 'GET',
    path: '/api/attendance/overtime/pending/approvals',
    owner: 'Overtime approval worklist',
    purpose:
      'Return assigned or delegated manager-stage requests and tenant-scoped Attendance Administrator-stage requests with readable actors and stage counts.',
  },
  {
    method: 'POST',
    path: '/api/attendance/overtime/requests/:requestId/approve',
    owner: 'Overtime approval decision',
    purpose:
      'Derive the current stage from persisted status, enforce assignment, subject and prior-actor separation, and apply the decision with compare-and-set concurrency.',
  },
  {
    method: 'GET',
    path: '/api/attendance/overtime/comp-off',
    owner: 'Compensatory-time worklist',
    purpose:
      'Return tenant-scoped, day-based credits with effective expiry, readable employee identity, bounded pagination, and employee/status/date filters.',
  },
  {
    method: 'GET',
    path: '/api/attendance/overtime/comp-off/employee/:employeeId/statistics',
    owner: 'Compensatory-time balance summary',
    purpose:
      'Calculate earned, available, used, expired, cancelled, and expiring-soon values on the server for one tenant-owned employee.',
  },
  {
    method: 'POST',
    path: '/api/attendance/overtime/comp-off/:compOffId/cancel',
    owner: 'Compensatory-time correction',
    purpose:
      'Cancel one active unused credit with scope, expiry, reason, compare-and-set, transactional outbox, and durable HRMS reversal controls.',
  },
  {
    method: 'POST',
    path: '/api/attendance/advanced/overtime-preapproval',
    owner: 'Retired duplicate overtime submission',
    purpose:
      'Return `410 Gone` with `/api/attendance/overtime/requests` as the canonical replacement. Existing legacy rows are preserved for a controlled drain and are not deleted by this endpoint.',
  },
  {
    method: 'GET',
    path: '/api/auth/abac/roles/assignment-options',
    owner: 'Role assignment',
    purpose:
      'Return server-authoritative direct, approval-required, or unavailable role options and allowed scopes for one tenant-owned target user.',
  },
  {
    method: 'POST',
    path: '/api/auth/abac/governance/requests/:id/cancel',
    owner: 'Requester cancellation for pending role-governance requests',
    purpose: 'Cancel an unresolved role-governance request before approval or rejection.',
  },
  {
    method: 'GET',
    path: '/api/hrms/legal-entities',
    owner: 'Company foundation',
    purpose: 'List tenant-owned companies or subsidiaries used by setup and employee creation.',
  },
  {
    method: 'GET',
    path: '/api/hrms/employees',
    owner: 'Employee directory',
    purpose:
      'List the permitted filtered employee population with a stable total plus `page[size]` and opaque `page[after]` cursor pagination.',
  },
  {
    method: 'POST',
    path: '/api/hrms/employees',
    owner: 'Employee master data',
    purpose:
      'Create an employee after legal entity, location, department, designation, and position are valid; optional `userId` links an existing active tenant user account instead of provisioning a second auth identity.',
  },
  {
    method: 'GET',
    path: '/api/hrms/employees/bulk/import-template',
    owner: 'Employee import preparation',
    purpose:
      'Download the current supported columns, instructions, and picklists before routine import or initial migration.',
  },
  {
    method: 'POST',
    path: '/api/hrms/employees/bulk/import',
    owner: 'Routine employee import',
    purpose:
      'Validate or apply incremental employee rows without historical employee-code reconciliation.',
  },
  {
    method: 'POST',
    path: '/api/hrms/employees/bulk/migrate',
    owner: 'Initial employee migration',
    purpose:
      'Persist and return a complete historical code plan during dry run, then apply only the unchanged approved plan with migration and code-override authorization.',
  },
  {
    method: 'GET',
    path: '/api/hrms/employees/:id/profile-image-url',
    owner: 'Employee profile image',
    purpose:
      'Return a profile-image URL or null through employee-read or owner-scoped access before HRMS storage handoff.',
  },
  {
    method: 'GET',
    path: '/api/hrms/hierarchy/managed-employees',
    owner: 'Manager target population',
    purpose:
      'Return active employees in the signed-in actor’s role-derived direct-report, led-team, or managed-department population; the service enforces tenant and scope boundaries.',
  },
  {
    method: 'GET',
    path: '/api/hrms/dashboard/{stats,activity,quick-actions,onboarding}',
    owner: 'Tenant workforce dashboard',
    purpose:
      'Return tenant-wide workforce metrics and setup activity only to actors with `hrms:dashboard:read:tenant`; manager dashboards use the managed-employee endpoint instead.',
  },
  {
    method: 'GET',
    path: '/api/hrms/holidays?includeInactive=true',
    owner: 'Holiday calendar setup',
    purpose:
      'List active and inactive tenant holiday rows for HR setup review while employee-facing holiday logic remains active-only.',
  },
  {
    method: 'POST',
    path: '/api/hrms/holidays',
    owner: 'Holiday calendar setup',
    purpose:
      'Create a tenant holiday row after validating location scope against active tenant-owned locations.',
  },
  {
    method: 'POST',
    path: '/api/hrms/holidays/bulk',
    owner: 'Holiday calendar import',
    purpose:
      'Import multiple holiday rows; invalid location-scoped rows are skipped with reasons instead of being trusted by downstream leave, attendance, or payroll consumers.',
  },
  {
    method: 'GET',
    path: '/api/hrms/holidays/working-days',
    owner: 'Holiday calendar consumers',
    purpose:
      'Calculate working days from tenant holiday policy and employee location context; employee-specific requests fail closed when the employee is outside the tenant.',
  },
  {
    method: 'Internal',
    path: 'Attendance holiday consumers',
    owner: 'Attendance roster and overtime engines',
    purpose:
      'Use active non-optional holiday rows with company-wide plus matching employee location/department scope when generating rosters, evaluating attendance, and calculating holiday overtime.',
  },
  {
    method: 'GET',
    path: '/api/shift-roster/schedules',
    owner: 'Shift roster self-service',
    purpose:
      'Return tenant schedules for Attendance Administrator permissions or clamp employee self-service reads to the authenticated employee record.',
  },
  {
    method: 'GET',
    path: '/api/shift-roster/templates/options',
    owner: 'Shift change self-service',
    purpose:
      'Return only active template labels and times needed by a permitted change-request form without exposing tenant template administration or statistics.',
  },
  {
    method: 'POST',
    path: '/api/shift-roster/schedules/generate',
    owner: 'Shift roster generation',
    purpose:
      'Generate draft roster rows with `skipHolidays: true` by default, skipping only company-wide or matching employee-location holidays unless the template is holiday-applicable or an explicit override is used.',
  },
  {
    method: 'POST',
    path: '/api/shift-roster/schedules/{publish,lock,unlock,unpublish}',
    owner: 'Shift roster lifecycle',
    purpose:
      'Move draft, published, or locked roster rows between lifecycle states after validating active tenant employee scope and a bounded 366-day planning window.',
  },
  {
    method: 'POST',
    path: '/api/shift-swap/post, /api/shift-swap/:swapId/claim',
    owner: 'Shift swap lifecycle',
    purpose:
      'Post or claim shift swaps after validating tenant-owned preferred employees, location policy, published active roster state, and duplicate claim prevention.',
  },
  {
    method: 'PATCH',
    path: '/api/shift-swap/claims/:claimId/{accept,reject,approve,deny}',
    owner: 'Shift swap lifecycle',
    purpose:
      'Move pending shift-swap claims through requester and manager decisions while blocking stale claims and manager self-approval.',
  },
  {
    method: 'GET',
    path: '/api/shift-swap/{available,my-posts,my-claims,pending-approvals,policy}',
    owner: 'Shift swap lifecycle',
    purpose:
      'List available, personal, approval, and policy views after filtering restricted swaps, location policy, and manager participation.',
  },
  {
    method: 'Internal',
    path: 'HRMS payroll ResolveInputsStep',
    owner: 'Payroll pipeline',
    purpose:
      'Resolve published or locked night/weekend/holiday roster rows, including `SWAPPED` rows, into configured-allowance `SHIFT_PREMIUM` earnings for the employee who owns the schedule at payroll run time.',
  },
  {
    method: 'DELETE',
    path: '/api/hrms/holidays/:id',
    owner: 'Holiday calendar setup',
    purpose:
      'Archive holidays with employee optional-holiday selections and physically remove only unused setup rows.',
  },
  {
    method: 'GET',
    path: '/api/hrms/employees/:id/documents',
    owner: 'Employee documents',
    purpose:
      'List tenant-owned employee document metadata after bootstrap grants tenant admin and HR Specialist roles explicit document action access; employee self access is owner-scoped.',
  },
  {
    method: 'POST',
    path: '/api/hrms/employees/:id/documents/presign',
    owner: 'Employee documents',
    purpose:
      'Create a validated upload handoff for tenant admins and HR specialists before direct object-storage upload.',
  },
  {
    method: 'POST',
    path: '/api/hrms/employees/:id/documents/:documentId/verify',
    owner: 'Employee documents',
    purpose: 'Mark uploaded evidence verified through tenant-scoped HR review permissions.',
  },
  {
    method: 'GET',
    path: '/api/hrms/employees/:id/joining-profile',
    owner: 'Employee joining-kit profile',
    purpose:
      'Read tenant-scoped joining-kit declarations, calculated same/other-industry experience totals, family particulars with marital status, nominees, and references for the selected employee.',
  },
  {
    method: 'PATCH',
    path: '/api/hrms/employees/:id/joining-profile',
    owner: 'Employee joining-kit profile',
    purpose:
      'Save joining-kit profile details before generated onboarding documents are produced; experience totals are calculated from prior-experience rows.',
  },
  {
    method: 'POST',
    path: '/api/hrms/employees/:id/documents/joining-kit/generate',
    owner: 'Joining kit generation',
    purpose:
      'Generate a saved-template joining-kit PDF for a tenant employee; HR Specialist or tenant-admin permission is required.',
  },
  {
    method: 'GET',
    path: '/api/hrms/payroll/admin/employees/:employeeId/payment-information',
    owner: 'Payroll Manager payment information',
    purpose:
      'List masked payroll-owned employee payment records; HR Specialist and Employee self-service users are denied.',
  },
  {
    method: 'POST',
    path: '/api/hrms/payroll/admin/employees/:employeeId/payment-information',
    owner: 'Payroll Manager payment information',
    purpose:
      'Create date-valid bank, UPI, wire, cheque, cash, or other payment records with method-specific validation and masked audit values.',
  },
  {
    method: 'POST',
    path: '/api/hrms/document-signature-webhooks/:tenantId/docusign',
    owner: 'Document signatures',
    purpose: 'Receive verified DocuSign callbacks and preserve provider evidence.',
  },
  {
    method: 'POST',
    path: '/api/hrms/employees/:id/documents/:documentId/signatures/:signatureTaskId/provider/void',
    owner: 'HR provider envelope recovery',
    purpose: 'Void an active provider envelope with a recorded HR recovery reason.',
  },
  {
    method: 'GET',
    path: '/api/hrms/audit/document-evidence/:employeeId/:documentId',
    owner: 'Document audit',
    purpose: 'Download the employee document evidence package for HR and audit review.',
  },
] as const satisfies readonly ApiReference[];

const securityControls = [
  {
    icon: Code2,
    title: 'Database baselines are deliberate',
    body: 'Before production-live, staging can be reset to one Prisma-generated baseline migration when approved. After the baseline, all schema changes must use Prisma-generated migrations and reviewed deploy evidence.',
  },
  {
    icon: Network,
    title: 'Staging deploys pull prebuilt images',
    body: 'Staging runtime hosts should pull prebuilt ECR images and restart containers. Do not compile multi-service Docker images on the shared EC2 host; use GitHub Actions or a scoped local/operator builder, then record health and browser evidence.',
  },
  {
    icon: ShieldCheck,
    title: 'ABAC is the source of truth',
    body: 'Frontend visibility is never the permission boundary. Gateway and service guards enforce tenant, role, and owner access. Nested route permissions use module:resource[:sub-resource...]:action:scope[:field], so backend policy checks parse the final pre-scope token as the action. Fresh tenant bootstrap derives explicit policies for non-CRUD system-role actions such as employee document reads and reviews across operational roles, while employee users keep owner-scoped self-service document access only. Employee-derived roles include tenant profile and HRMS gateway admission for app-shell context, but tenant settings stay tenant-admin-owned. Request a protected assignment from User & Security > Users > Manage roles, then review it under User & Security > Roles & Permissions. When another active governance approver exists, the requester can cancel but cannot review their own request. A true sole approver instead receives Approve as sole admin, which requires current-password step-up and records the exception. Once a second approver exists, independent approval applies and the last approver cannot be removed. Role, user, department, and team names are shown in these operator views; technical IDs stay in audit evidence.',
  },
  {
    title: 'MFA and passkeys are rollout gates',
    body: 'Password policy, sessions, MFA, passkeys, email verification, and role governance are baseline security on every plan. Privileged first login now requires TOTP enrollment before tokens are issued. Tenant-admin factor recovery, passkey reset, session revocation, and governance approval require password step-up. Maker-checker applies when another governance approver exists, and true one-admin tenants get audited solo-admin approval. Phone verification can stay disabled only while SMS DLT approval is pending.',
    icon: LockKeyhole,
  },
  {
    title: 'Sign-in recovery is scoped and audited',
    body: 'Tenant admins reset only an active same-tenant user cooldown after identity verification, password step-up, and reason capture. Superadmins first select a tenant and search readable user details. Failed attempts remain in history; shared IP controls and service circuit breakers remain platform operations.',
    icon: ShieldCheck,
  },
  {
    title: 'Events and provider callbacks are audited',
    body: 'Outbox events, DocuSign callbacks, document downloads, void/retry actions, and evidence packages must be visible in operations or documented as intentionally internal.',
    icon: Network,
  },
  {
    icon: KeyRound,
    title: 'Provider callbacks are verified',
    body: 'Embedded signatures stamp and store a signed PDF artifact in protected signature-evidence storage. DocuSign callbacks preserve the raw payload, require HMAC verification plus a replay-window timestamp, accept staged HMAC secrets during rotation, use provider OAuth downloads before missing signed evidence is stored, and expose signer placement, sandbox proof commands, audit packages, and HR recovery controls for live or failed envelopes.',
  },
] as const;

const troubleGuides = [
  {
    title: 'I signed up but cannot log in',
    symptom: 'The company or admin email is still pending verification.',
    resolution:
      'Check the configured email provider. In local development, use console email output. Then complete both company and admin verification before signing in.',
  },
  {
    title: 'Employee code is not what I expected',
    symptom: 'The employee was created before the legal entity or code rule was configured.',
    resolution:
      'Create the company foundation and employee-code rule first. Signup does not reserve employee codes; employee creation should generate codes from selected company context. The serial runs per company/legal entity and does not reset every joining year.',
  },
  {
    title: 'My employee migration preview expired',
    symptom:
      'Apply reports that the plan is stale after the file, employee population, or company code sequence changed.',
    resolution:
      'Return to the source workbook, keep the complete company population, and run Validate again. Review the replacement plan in full; do not reuse a plan ID from an earlier file or sequence snapshot.',
  },
  {
    title: 'An existing employee is missing from initial migration',
    symptom:
      'The preview cannot safely reconcile an existing employee or the complete historical order is ambiguous.',
    resolution:
      'Include every existing employee for the selected legal entity, match the maintained email exactly, and use Migration Sequence only for same-date ties. Do not apply a partial historical file.',
  },
  {
    title: 'A regularization route needs recovery',
    symptom:
      'The request says Configuration Required, or a previously assigned approver is now inactive, outside the reporting chain, or missing the required approval permission.',
    resolution:
      'Correct the reporting manager, user, delegation, Attendance Administrator, or Governance setup as appropriate. Then an Attendance Administrator or Tenant Administrator opens Attendance > Regularization > Approvals, opens the request, chooses Recover Approval Route, and records why. Governance cannot reroute unassigned work, and a healthy route cannot be replaced.',
  },
  {
    title: 'An overtime request cannot be submitted',
    symptom:
      'Submission reports that no overtime policy applies, or the retained request shows Approval setup required because an independent Governance or Attendance Administrator actor is missing.',
    resolution:
      'Ask an Attendance Administrator to create or assign an active policy that covers the employee and date, with pre-approval enabled. Ask HR to maintain the reporting hierarchy and effective manager role. For a genuine top-hierarchy or unresolved-manager exception, assign an MFA-protected Attendance Governance Approver. When policy requires another review, assign an independent Attendance Administrator; Governance cannot fill both stages. Read the request details for the exact missing actor. Do not paste UUIDs, edit shared data, or change policy to bypass an in-flight route.',
  },
  {
    title: 'A Comp-Off balance or cancellation is still pending',
    symptom:
      'The Attendance credit changed state, but the HRMS COMP_OFF balance has not yet reflected the earned or cancelled posting.',
    resolution:
      'Do not edit the leave balance or retry the browser action repeatedly. Check Settings > System > Events for the earned or cancelled event and its durable HRMS consumer state. Cancellation waits for the source credit posting and then retries idempotently; escalate a persistent dead-letter or missing COMP_OFF leave type to an Attendance or HRMS operator with the event and credit IDs.',
  },
  {
    title: 'Phone OTP is skipped locally',
    symptom:
      'SMS DLT templates are not approved, so phone verification is disabled by environment.',
    resolution:
      'Use `REQUIRE_PHONE_VERIFICATION=false` only for local or approved staging bypass. Keep email verification, MFA, and passkeys in the rollout plan.',
  },
  {
    title: 'Sign-in asks me to set up MFA',
    symptom:
      'Your role or tenant policy requires stronger authentication and no TOTP factor is active yet.',
    resolution:
      'Use the Set up MFA screen from login, scan the QR code with an authenticator app, or open "can’t scan" to enter the setup key manually. Enter the current code, save or download the backup codes, and continue. The enrollment challenge is short-lived and bound to the browser session.',
  },
  {
    title: 'A user is blocked after failed sign-in attempts',
    symptom:
      'The login response says the account is temporarily locked or there were too many failed attempts.',
    resolution:
      'A permitted tenant admin opens Authentication > Security Operations or Users & Security > Users, verifies the person, and chooses Reset sign-in cooldown. Superadmins use the selected tenant User Recovery tab. Enter the recovery reason and current administrator password; do not paste a UUID or reset shared IP/service state.',
  },
  {
    title: 'Users and Security says Last sign-in: Never',
    symptom: 'The account has reached the dashboard before, but its user row still says Never.',
    resolution:
      'Reload the user directory after a completed session. The timestamp must come from the canonical authentication profile and changes only after token issuance. A correct password followed by an unfinished MFA challenge, or an invited account that never finished sign-in, correctly remains Never.',
  },
  {
    title: 'Ordinary navigation says Too many requests',
    symptom:
      'Normal signed-in browsing returns HTTP 429 even though the user did not enter a wrong password.',
    resolution:
      'This is a Gateway traffic limit, not an account cooldown. Keep the session, wait for the Retry-After period, and avoid repeated refreshes. Reset sign-in cooldown applies only to an active credential-failure restriction on one verified user.',
  },
  {
    title: 'Passkey prompt does not appear',
    symptom:
      'The browser does not show Touch ID, Face ID, Windows Hello, phone QR, password manager, or security-key options.',
    resolution:
      'Use a passkey-capable browser over HTTPS, check that the tenant has passkeys enabled, and verify that `FRONTEND_URL` plus `CORS_ORIGINS` include the exact browser origin such as `https://staging.unitree.mvpl.info`.',
  },
  {
    title: 'A setup option is missing from the UI',
    symptom:
      'The selected role does not have the required ABAC permission or the parent foundation object is inactive.',
    resolution:
      'Check the role, tenant, legal entity, location, and object status. Do not treat hidden buttons as the only control; verify the API response and audit log.',
  },
  {
    title: 'Search finds an employee that normal directory pages do not show',
    symptom:
      'The Employees summary reports more people than the table can page through, but searching by name returns the missing record.',
    resolution:
      'This indicates that a client treated one bounded server page as the full directory. The table must use the response total, send `page[size]`, follow `nextCursor` through Next and Previous, and reset its cursor history after search or filter changes. Do not recreate the employee record.',
  },
  {
    title: 'A manager page shows 403 or unrelated departments',
    symptom:
      'A People Manager, Team Lead, or Department Head opens Attendance, Timesheets, My Team, or Overtime and sees a forbidden request, tenant-wide data, or departments outside their assignment.',
    resolution:
      'Verify the persisted system role and formal reporting, led-team, or managed-department assignment. People Manager uses direct reports, Team Lead uses active teams they formally lead, and Department Head uses the managed department. The UI must not call `/api/reports/dashboard`, `/api/reports/attendance-register`, `/api/overtime/policies`, or tenant HRMS dashboard endpoints unless the actor has the matching tenant permission. Scoped department filters must derive from `/api/hrms/hierarchy/managed-employees`, not the tenant department catalog.',
  },
  {
    title: 'A holiday still appears after archive',
    symptom:
      'An optional holiday was archived from setup but still appears with an inactive marker.',
    resolution:
      'This is expected when employees already selected the optional holiday. UniTree deactivates the holiday to preserve selection history; unused setup rows can still be physically removed.',
  },
  {
    title: 'A regional holiday affects the wrong shift or overtime record',
    symptom:
      'An employee without the matching location sees a roster skip or holiday overtime premium for a regional holiday.',
    resolution:
      'Verify the employee location and holiday location scope. Attendance consumers use active non-optional company-wide holidays plus holidays matching the employee location/department; employees without a location receive company-wide holidays only.',
  },
  {
    title: 'Roster generation skips or creates a holiday shift unexpectedly',
    symptom:
      'Generate Schedules either skips a holiday you expected to staff or creates a shift on a holiday you expected to stay non-working.',
    resolution:
      'Check the holiday scope, employee location, and shift template. The UI sends `skipHolidays: true` by default, regional holidays apply only to matching employee locations, employees without a location receive company-wide holidays only, holiday-applicable templates can still create holiday shifts, and API/operator overrides may intentionally send `skipHolidays: false`.',
  },
  {
    title: 'Roster publish or lock returns a validation error',
    symptom:
      'Publishing, locking, unlocking, or unpublishing a shift roster returns 400 before any schedule state changes.',
    resolution:
      'Check that every employee ID belongs to an active employee in the tenant, remove blanks or duplicates, and keep each lifecycle request within a 366-day planning window. The backend rejects unsafe lifecycle updates even when the UI button is visible.',
  },
  {
    title: 'Employee My Shifts is empty or forbidden',
    symptom:
      'An employee opens `/shifts` and sees missing schedules, 403 responses, or admin roster/template calls in the browser network log.',
    resolution:
      'Confirm the user is linked to an active tenant employee and has the current Employee system role. The employee view should call only `/api/shift-roster/schedules` with the authenticated employee ID; older tenants may need the current ABAC role sync so `attendance:shift_schedule:read:self` is present.',
  },
  {
    title: 'A shift swap is hidden, cannot be claimed, or cannot be approved',
    symptom:
      'A posted swap does not appear for an employee, claim creation returns 400, or a manager sees a pending approval but approve/deny is rejected.',
    resolution:
      'Use `/api/shift-swap` for employee swap postings and claims. The retired directed `/api/shift-roster/change-requests/swap` path is no longer exposed, and generic shift change requests reject `changeType: SWAP`. Check preferred employees, employee location, swap policy, and participation: restricted swaps are visible only to preferred active tenant employees; no-location claimers cannot claim location-bound shifts unless cross-location swaps are enabled; requester decisions require pending claims; and managers cannot review swaps where they are the requester or claimer.',
  },
  {
    title: 'A swapped night or weekend shift is missing from payroll',
    symptom:
      'A shift swap completed, but the payroll run does not show the expected night, weekend, or holiday premium.',
    resolution:
      'Confirm the roster row is published or locked, still assigned to the swapped employee, has status `SWAPPED`, `SCHEDULED`, `CONFIRMED`, or `COMPLETED`, and the shift template has a positive night, weekend, or holiday allowance percentage. Payroll posts premiums as `SHIFT_PREMIUM` earnings from the HRMS payroll input snapshot and keeps overtime hours separate.',
  },
  {
    title: 'Employee Documents tab says forbidden',
    symptom:
      'A freshly created tenant admin can open employee detail but the employee documents request returns 403.',
    resolution:
      'Confirm the tenant was bootstrapped with current ABAC policies and that the role includes explicit employee document actions. After foundation setup, tenant-admin and HR Specialist document reads should return 200, while employee users should receive 403 for cross-employee document reads or tenant upload attempts.',
  },
  {
    title: 'HR Specialist employee detail shows forbidden calls',
    symptom:
      '`/api/tenant/current` or `/api/hrms/employees/:id/profile-image-url` returns 403 while the employee detail page otherwise opens.',
    resolution:
      'Run the current ABAC role sync for older tenants and confirm HR Specialist inherits the employee self-service/admission bundle. The profile-image route should be explicit employee-read access, tenant settings should remain tenant-admin-only, and the Payment tab should remain hidden from HR Specialist.',
  },
  {
    title: 'An admin has no employee profile',
    symptom:
      'The tenant admin or another active user account can sign in, but Profile shows no employee profile and employee self-service pages are empty or unavailable.',
    resolution:
      'Do not edit auth IDs directly. From `Settings > Users & Security > Users`, search by readable user details, open the active unlinked account actions menu, and choose `Create linked employee profile`. The same action appears on `Settings > Profile` for the current unlinked admin when they have employee-create permission.',
  },
  {
    title: 'Staging deploy hangs or SSH times out',
    symptom:
      'A manual Docker build was started on the staging EC2 host and SSH, SSM, or public HTTPS becomes slow or unavailable.',
    resolution:
      'Stop the in-place build, verify EC2/SSM/Compose health, and use prebuilt ECR images instead. Build through GitHub Actions or a scoped local/operator builder with `UNITREE_STAGING_SERVICES`, then deploy by image pull and container restart.',
  },
] as const satisfies readonly TroubleGuide[];

const methodClassName: Record<ApiReference['method'], string> = {
  GET: 'border-sky-200 bg-sky-50 text-sky-800',
  POST: 'border-teal-200 bg-teal-50 text-teal-800',
  PATCH: 'border-amber-200 bg-amber-50 text-amber-800',
  DELETE: 'border-rose-200 bg-rose-50 text-rose-800',
  Internal: 'border-slate-200 bg-slate-50 text-slate-800',
};

function normalizeSearch(value: string) {
  return value.trim().toLowerCase();
}

function topicSearchText(topic: DocTopic) {
  return [
    topic.title,
    topic.description,
    topic.category,
    topic.role,
    topic.path,
    ...topic.keywords,
    ...topic.steps,
    ...topic.checks,
    ...topic.references,
  ]
    .join(' ')
    .toLowerCase();
}

function topicMatches(topic: DocTopic, query: string, selectedRole: RoleFilter) {
  const roleMatches = selectedRole === 'All roles' || topic.role === selectedRole;
  const queryMatches = query.length === 0 || topicSearchText(topic).includes(query);

  return roleMatches && queryMatches;
}

function SectionHeading({
  eyebrow,
  title,
  body,
  icon: Icon,
}: {
  eyebrow: string;
  title: string;
  body: string;
  icon: LucideIcon;
}) {
  return (
    <div className="max-w-3xl">
      <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold uppercase tracking-normal text-teal-800">
        <Icon className="h-3.5 w-3.5" />
        {eyebrow}
      </div>
      <h2 className="mt-4 text-2xl font-semibold tracking-normal text-zinc-950 sm:text-3xl">
        {title}
      </h2>
      <p className="mt-3 text-base leading-7 text-zinc-600">{body}</p>
    </div>
  );
}

function QuickStartCard({
  step,
  index,
}: {
  step: (typeof quickStartSteps)[number];
  index: number;
}) {
  const Icon = step.icon;

  return (
    <article className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-zinc-950 text-white">
          <Icon className="h-5 w-5" />
        </div>
        <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
          Step {index + 1}
        </span>
      </div>
      <h3 className="mt-5 text-base font-semibold text-zinc-950">{step.title}</h3>
      <p className="mt-2 text-sm font-medium text-teal-700">{step.owner}</p>
      <p className="mt-3 text-sm leading-6 text-zinc-600">{step.body}</p>
    </article>
  );
}

function TopicCard({ topic }: { topic: DocTopic }) {
  const Icon = topic.icon;

  return (
    <article
      id={topic.id}
      className="min-w-0 max-w-full scroll-mt-24 rounded-lg border border-zinc-200 bg-white p-5 shadow-sm"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-teal-200 bg-teal-50 text-teal-800">
              {topic.category}
            </Badge>
            <Badge variant="secondary" className="bg-zinc-100 text-zinc-700">
              {topic.role}
            </Badge>
            <span className="text-xs text-zinc-500">Updated {topic.updated}</span>
          </div>
          <h3 className="mt-4 text-xl font-semibold tracking-normal text-zinc-950">
            {topic.title}
          </h3>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-zinc-600">{topic.description}</p>
        </div>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 text-zinc-800">
          <Icon className="h-5 w-5" />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div>
          <h4 className="text-sm font-semibold text-zinc-950">Workflow</h4>
          <ol className="mt-3 space-y-3">
            {topic.steps.map((step, index) => (
              <li key={step} className="flex gap-3 text-sm leading-6 text-zinc-600">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-950 text-xs font-semibold text-white">
                  {index + 1}
                </span>
                <span className="min-w-0 break-words">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-zinc-950">Closeout checks</h4>
          <ul className="mt-3 space-y-3">
            {topic.checks.map((check) => (
              <li key={check} className="flex gap-3 text-sm leading-6 text-zinc-600">
                <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-teal-700" />
                <span className="min-w-0 break-words">{check}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3 border-t border-zinc-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-normal text-zinc-500">UI path</p>
          <code className="mt-1 block break-words rounded-md bg-zinc-100 px-2 py-1 text-xs text-zinc-800">
            {topic.path}
          </code>
        </div>
        <div className="flex flex-wrap gap-2">
          {topic.references.map((reference) => (
            <span
              key={reference}
              className="max-w-full break-all rounded-full border border-zinc-200 px-2.5 py-1 text-xs text-zinc-600"
            >
              {reference}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

function ApiReferenceTable() {
  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
      <div className="max-w-full overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="px-4 py-3 font-semibold">Method</th>
              <th className="px-4 py-3 font-semibold">Route</th>
              <th className="px-4 py-3 font-semibold">Owner</th>
              <th className="px-4 py-3 font-semibold">Purpose</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {apiReferences.map((row) => (
              <tr key={`${row.method}-${row.path}`} className="align-top">
                <td className="px-4 py-4">
                  <span
                    className={`inline-flex rounded-full border px-2.5 py-1 font-mono text-xs font-semibold ${methodClassName[row.method]}`}
                  >
                    {row.method}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <code className="break-words rounded-md bg-zinc-100 px-2 py-1 text-xs text-zinc-800">
                    {row.path}
                  </code>
                </td>
                <td className="px-4 py-4 font-medium text-zinc-900">{row.owner}</td>
                <td className="px-4 py-4 leading-6 text-zinc-600">{row.purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SecurityControlCard({ item }: { item: (typeof securityControls)[number] }) {
  const Icon = item.icon;

  return (
    <article className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <Icon className="h-6 w-6 text-teal-700" />
      <h3 className="mt-4 text-base font-semibold text-zinc-950">{item.title}</h3>
      <p className="mt-3 text-sm leading-6 text-zinc-600">{item.body}</p>
    </article>
  );
}

function TroubleGuideCard({ guide }: { guide: TroubleGuide }) {
  return (
    <article className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <h3 className="text-base font-semibold text-zinc-950">{guide.title}</h3>
          <p className="mt-2 text-sm leading-6 text-zinc-600">
            <span className="font-semibold text-zinc-800">Symptom:</span> {guide.symptom}
          </p>
          <p className="mt-2 text-sm leading-6 text-zinc-600">
            <span className="font-semibold text-zinc-800">Resolution:</span> {guide.resolution}
          </p>
        </div>
      </div>
    </article>
  );
}

export default function PublicDocsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<RoleFilter>('All roles');

  const normalizedQuery = normalizeSearch(searchQuery);
  const filteredTopics = useMemo(
    () => docTopics.filter((topic) => topicMatches(topic, normalizedQuery, selectedRole)),
    [normalizedQuery, selectedRole],
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSelectedRole('All roles');
  };

  return (
    <div className="min-h-screen bg-[#f8faf9] text-zinc-950">
      <header className="sticky top-0 z-30 border-b border-zinc-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-950 text-sm font-semibold text-white">
              UT
            </span>
            <span className="text-lg font-semibold">
              UniTree<span className="hidden sm:inline"> Docs</span>
            </span>
          </Link>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" className="px-3">
                Sign in
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-teal-700 px-3 text-white hover:bg-teal-800 sm:px-4">
                <UserRoundPlus className="mr-2 h-4 w-4" />
                Start setup
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="border-b border-zinc-200 bg-white">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[minmax(0,1.05fr)_360px] lg:px-8">
            <div className="min-w-0">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-950"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to home
              </Link>
              <div className="mt-8 max-w-4xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-normal text-amber-800">
                  <Sparkles className="h-3.5 w-3.5" />
                  Public product docs
                </div>
                <h1 className="mt-5 text-4xl font-semibold tracking-normal text-zinc-950 sm:text-5xl">
                  Set up UniTree without private hand-holding.
                </h1>
                <p className="mt-5 max-w-3xl text-lg leading-8 text-zinc-600">
                  Search the tenant setup, HR foundation, employee lifecycle, documents, security,
                  and API guides that define the production operating sequence.
                </p>
              </div>

              <div className="mt-8 max-w-3xl rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                <label htmlFor="docs-search" className="sr-only">
                  Search UniTree docs
                </label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="docs-search"
                    type="search"
                    role="searchbox"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search signup, passkeys, employee code, joining kit, API..."
                    className="h-12 border-zinc-300 bg-white pl-10 text-base"
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-2" aria-label="Filter docs by role">
                  {roleFilters.map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => setSelectedRole(role)}
                      className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                        selectedRole === role
                          ? 'border-zinc-950 bg-zinc-950 text-white'
                          : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400'
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <aside className="rounded-lg border border-zinc-200 bg-zinc-950 p-5 text-white shadow-sm">
              <BookOpen className="h-7 w-7 text-teal-300" />
              <h2 className="mt-4 text-lg font-semibold">Docs closeout contract</h2>
              <p className="mt-3 text-sm leading-6 text-zinc-300">
                Every behavior-changing slice must update public docs, README, wiki, closeout notes,
                and handoff files before the module is treated as current-scope complete.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                  <p className="text-2xl font-semibold">{docTopics.length}</p>
                  <p className="mt-1 text-zinc-300">guides</p>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                  <p className="text-2xl font-semibold">{apiReferences.length}</p>
                  <p className="mt-1 text-zinc-300">API routes</p>
                </div>
              </div>
              <Link to="/signup" className="mt-6 inline-flex w-full">
                <Button className="w-full bg-white text-zinc-950 hover:bg-zinc-100">
                  Start tenant setup
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </aside>
          </div>
        </section>

        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:px-8">
          <aside className="hidden lg:block">
            <nav className="sticky top-24 space-y-1" aria-label="Public docs sections">
              {navigationSections.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="block rounded-md px-3 py-2 text-sm font-medium text-zinc-600 hover:bg-white hover:text-zinc-950"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </aside>

          <div className="min-w-0 space-y-14">
            <section id="quickstart" className="scroll-mt-24">
              <SectionHeading
                eyebrow="Setup sequence"
                title="Start with identity, then company structure, then employees."
                body="UniTree follows a foundation-first HR model. The tenant admin account is not the first employee; employee records and codes are created only after the tenant has company context, and the first active employee completes the dashboard employee step."
                icon={Building2}
              />
              <div className="mt-7 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {quickStartSteps.map((step, index) => (
                  <QuickStartCard key={step.title} step={step} index={index} />
                ))}
              </div>
            </section>

            <section id="guides" className="scroll-mt-24">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <SectionHeading
                  eyebrow="How-to guides"
                  title="Searchable, role-aware runbooks."
                  body="Each guide includes the UI path, workflow, server-side checks, and source references that must stay aligned with implementation."
                  icon={FileText}
                />
                <div className="rounded-lg border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-600 shadow-sm">
                  <span className="font-semibold text-zinc-950">{filteredTopics.length}</span> of{' '}
                  {docTopics.length} guides shown
                </div>
              </div>

              {filteredTopics.length > 0 ? (
                <div className="mt-7 grid grid-cols-[minmax(0,1fr)] gap-5">
                  {filteredTopics.map((topic) => (
                    <TopicCard key={topic.id} topic={topic} />
                  ))}
                </div>
              ) : (
                <div className="mt-7 rounded-lg border border-dashed border-zinc-300 bg-white p-8 text-center shadow-sm">
                  <Search className="mx-auto h-8 w-8 text-zinc-400" />
                  <h3 className="mt-4 text-lg font-semibold text-zinc-950">No docs matched</h3>
                  <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-600">
                    Try a broader term such as employee, setup, MFA, location, position, or
                    document.
                  </p>
                  <Button variant="outline" className="mt-5" onClick={clearSearch}>
                    Clear search
                  </Button>
                </div>
              )}
            </section>

            <section id="api" className="min-w-0 scroll-mt-24">
              <SectionHeading
                eyebrow="Integration"
                title="Core public and setup APIs."
                body="The gateway remains the public boundary. Service internals count only when documented as intentionally internal and covered by tests."
                icon={Code2}
              />
              <div className="mt-7 min-w-0 max-w-full">
                <ApiReferenceTable />
              </div>
            </section>

            <section id="security" className="scroll-mt-24">
              <SectionHeading
                eyebrow="Security"
                title="Identity, authorization, and audit are setup tasks."
                body="A tenant is not ready for production just because signup works. Security policy, role governance, events, and recovery controls need visible operating surfaces."
                icon={Fingerprint}
              />
              <div className="mt-7 grid gap-4 md:grid-cols-3">
                {securityControls.map((item) => (
                  <SecurityControlCard key={item.title} item={item} />
                ))}
              </div>
            </section>

            <section id="troubleshooting" className="scroll-mt-24">
              <SectionHeading
                eyebrow="Support"
                title="Common first-run blockers."
                body="These are the problems most likely to appear while local, staging, and first tenant setup are still being hardened."
                icon={LifeBuoy}
              />
              <div className="mt-7 grid gap-4 md:grid-cols-2">
                {troubleGuides.map((guide) => (
                  <TroubleGuideCard key={guide.title} guide={guide} />
                ))}
              </div>
            </section>
          </div>
        </div>
      </main>

      <footer className="border-t border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-8 text-sm text-zinc-600 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
          <span>UniTree public docs</span>
          <div className="flex flex-wrap gap-5">
            <Link to="/signup" className="hover:text-zinc-950">
              Start setup
            </Link>
            <Link to="/login" className="hover:text-zinc-950">
              Sign in
            </Link>
            <a href="#api" className="hover:text-zinc-950">
              API routes
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
